﻿// Game constants and configuration

import { RackType, ServerType, TORSwitchType, ContractType, PowerSupplier, UpgradeType, Goal, Stats, LocationData, Floor, Rack, Upgrades, PowerGrid, PowerGrids, FloorQuality, ChillerType, StaffRoleConfig, MpoPatchPanelType, ManagementSwitchType, HdaSwitchType, CoreSwitchType, TransitProvider, IxpExchange, NawasTier, BackboneFiberType, RenewableType, ContractTemplate, ClientType, ContractTier } from './types';

// Upgrade types available in the game
export const UPGRADE_TYPES: UpgradeType[] = [
  {
    id: 'powerEfficiency',
    name: 'Power Efficiency',
    description: 'Reduce power consumption by 10% per level',
    baseCost: 15000,
    effect: '10% less power consumption',
    maxLevel: 10
  },
  {
    id: 'coolingEfficiency',
    name: 'Cooling Efficiency',
    description: 'Reduce cooling requirements by 10% per level',
    baseCost: 15000,
    effect: '10% less cooling needed',
    maxLevel: 10
  },
  {
    id: 'serverPerformance',
    name: 'Server Performance',
    description: 'Increase server revenue by 15% per level',
    baseCost: 25000,
    effect: '15% more revenue per server',
    maxLevel: 10
  },
  {
    id: 'networkInfrastructure',
    name: 'Network Infrastructure',
    description: 'Increase customer capacity by 20% per level',
    baseCost: 30000,
    effect: '20% more customers per server',
    maxLevel: 10
  },
  {
    id: 'businessDevelopment',
    name: 'Business Development',
    description: 'Faster reputation growth and better contracts',
    baseCost: 40000,
    effect: 'Faster reputation growth',
    maxLevel: 10
  },
  {
    id: 'disasterPreparedness',
    name: 'Disaster Preparedness',
    description: 'Reduce impact of random events by 30% per level',
    baseCost: 75000,
    effect: 'Reduce impact of random events',
    maxLevel: 10
  }
];

// Rack types available in the game
// All racks are 48U standard cabinets (APC NetShelter SX or equivalent).
// The variant is determined by the PDU installed, which sets the power budget.
// Power class:
//   5 kW  â€” 1-phase 32A   (APC AP7802 Basic PDU)
//   7.4 kW â€” 1-phase 32A  (APC AP8858 Switched Metered PDU â€” full 230VÃ—32A)
//   11 kW  â€” 3-phase 16A  (APC AP8868 Switched 3P PDU)
//   17 kW  â€” 3-phase 32A  (APC AP8878 Switched 3P PDU, single)
//   22 kW  â€” 3-phase 32A  (APC AP8878A Switched 3P PDU, dual/redundant)
export const RACK_TYPES: RackType[] = [
  {
    id: '48u-1p-5kw',
    name: '48U Â· 1P/32A Â· 5 kW',
    description: 'APC NetShelter SX 48U + AP7802 Basic 1-phase 32A PDU. 5 kW committed power. Suitable for lightweight web and file servers.',
    cost: 3500,
    capacity: 48,
    power: 5,
    cooling: 4.8,
    color: 'bg-gray-400',
    bonuses: null,
    family: 'standard'
  },
  {
    id: '48u-1p-7kw',
    name: '48U Â· 1P/32A Â· 7.4 kW',
    description: 'APC NetShelter SX 48U + AP8858 Switched Metered 1-phase 32A PDU. Full 7.4 kW (230VÃ—32A). Per-outlet switching and energy monitoring.',
    cost: 6500,
    capacity: 48,
    power: 7.4,
    cooling: 7.0,
    color: 'bg-blue-400',
    bonuses: { powerEfficiency: 1.05 },
    family: 'standard'
  },
  {
    id: '48u-3p-11kw',
    name: '48U Â· 3P/16A Â· 11 kW',
    description: 'APC NetShelter SX 48U + AP8868 Switched 3-phase 16A PDU. 11 kW (3Ã—230VÃ—16A). Good balance of power and cost for compute workloads.',
    cost: 10000,
    capacity: 48,
    power: 11,
    cooling: 10.5,
    color: 'bg-blue-600',
    bonuses: { powerEfficiency: 1.05 },
    family: 'standard'
  },
  {
    id: '48u-3p-17kw',
    name: '48U Â· 3P/32A Â· 17 kW',
    description: 'APC NetShelter SX 48U + AP8878 Switched 3-phase 32A PDU. 17 kW committed. Ideal for dense compute or mixed GPU workloads.',
    cost: 16000,
    capacity: 48,
    power: 17,
    cooling: 16.2,
    color: 'bg-purple-500',
    bonuses: { powerEfficiency: 1.08 },
    family: 'standard'
  },
  {
    id: '48u-3p-22kw',
    name: '48U Â· 3P/32A Â· 22 kW',
    description: 'APC NetShelter SX 48U + AP8878A Switched 3-phase 32A PDU. Full 22 kW (3Ã—230VÃ—32A). Top-tier single-PDU power budget for high-density GPU or HPC.',
    cost: 22000,
    capacity: 48,
    power: 22,
    cooling: 21,
    color: 'bg-purple-700',
    bonuses: { powerEfficiency: 1.1 },
    family: 'standard'
  },
  {
    id: '48u-3p-22kw-ab',
    name: '48U Â· 3P/32A Â· 22 kW (A+B)',
    description: 'APC NetShelter SX 48U with dual AP8878A PDUs on separate feeds (A+B redundancy). 22 kW usable. N+1 power path â€” best uptime for critical workloads.',
    cost: 35000,
    capacity: 48,
    power: 22,
    cooling: 21,
    color: 'bg-yellow-500',
    bonuses: { powerEfficiency: 1.15 },
    family: 'standard'
  }
];

// Server types â€” Dell PowerEdge lineup
// Power/cooling figures are realistic per-server averages (typical load).
// Cooling â‰ˆ 92% of power (most electrical energy becomes heat).
export const SERVER_TYPES: ServerType[] = [
  // â”€â”€ ENTRY TIER â”€â”€ fits comfortably in 5 kW racks â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // -- ENTRY TIER -- 1 GbE NIC, low traffic
  { id: 'r250',   name: 'Dell PowerEdge R250',   description: '1U entry server. Single Intel Xeon E-2300, 32 GB DDR4. Ideal for web hosting, DNS, lightweight apps.',                                                      cost: 2500,   powerDraw: 0.15, coolingNeeded: 0.14, revenue: 1800,  customers: 15, unlockLevel: 0, uSize: 1, contractCategory: 'webhosting', trafficGbps: 0.05,  nicSpeedGbps: 1   },
  { id: 'r350',   name: 'Dell PowerEdge R350',   description: '1U entry server with 8x NVMe bays. Single Xeon E-2300, up to 64 GB DDR4. Good for SMB file/app servers.',                                                   cost: 3500,   powerDraw: 0.20, coolingNeeded: 0.18, revenue: 2700,  customers: 20, unlockLevel: 0, uSize: 1, contractCategory: 'webhosting', trafficGbps: 0.08,  nicSpeedGbps: 1   },
  // -- MID TIER -- 10 GbE NIC
  { id: 'r450',   name: 'Dell PowerEdge R450',   description: '1U mid-range server. Single or dual Intel Xeon SP 3rd gen, up to 512 GB DDR4. Versatile app/compute server.',                                               cost: 7000,   powerDraw: 0.40, coolingNeeded: 0.37, revenue: 5700,  customers: 35, unlockLevel: 1, uSize: 1, contractCategory: 'colocation', trafficGbps: 0.5,   nicSpeedGbps: 10  },
  { id: 'r550',   name: 'Dell PowerEdge R550',   description: '2U mid-range server. Dual socket Xeon SP, 16x drive bays, up to 1 TB RAM. Strong DB and storage workloads.',                                               cost: 10000,  powerDraw: 0.60, coolingNeeded: 0.55, revenue: 8700,  customers: 30, unlockLevel: 1, uSize: 2, contractCategory: 'colocation', trafficGbps: 0.8,   nicSpeedGbps: 10  },
  // -- PERFORMANCE TIER -- 25 GbE NIC
  { id: 'r650',   name: 'Dell PowerEdge R650',   description: '1U performance server. Dual Xeon SP 3rd gen, 24x DDR4 DIMMs, 2x PCIe 4.0. Excellent virtualisation host.',                                                  cost: 14000,  powerDraw: 0.85, coolingNeeded: 0.78, revenue: 12300, customers: 25, unlockLevel: 2, uSize: 1, contractCategory: 'enterprise',  trafficGbps: 2.0,   nicSpeedGbps: 25  },
  { id: 'r650xs', name: 'Dell PowerEdge R650xs', description: '1U dense performance server. Dual Xeon SP 4th gen Ice Lake, 24 DIMM slots. Optimised for HPC and AI inference.',                                            cost: 18000,  powerDraw: 1.00, coolingNeeded: 0.92, revenue: 16500, customers: 20, unlockLevel: 2, uSize: 1, contractCategory: 'enterprise',  trafficGbps: 2.5,   nicSpeedGbps: 25  },
  { id: 'r750',   name: 'Dell PowerEdge R750',   description: '2U performance server. Dual Xeon SP 3rd gen, 4x PCIe 4.0, 24 DIMM slots. Primary enterprise virtualisation platform.',                                      cost: 22000,  powerDraw: 1.20, coolingNeeded: 1.10, revenue: 21000, customers: 15, unlockLevel: 3, uSize: 2, contractCategory: 'enterprise',  trafficGbps: 4.0,   nicSpeedGbps: 25  },
  { id: 'r750xs', name: 'Dell PowerEdge R750xs', description: '2U HPC-optimised server. Dual Xeon SP, NVLink-capable, up to 6 TB RAM. Designed for compute-intensive workloads.',                                           cost: 28000,  powerDraw: 1.50, coolingNeeded: 1.38, revenue: 27000, customers: 10, unlockLevel: 3, uSize: 2, contractCategory: 'enterprise',  trafficGbps: 5.0,   nicSpeedGbps: 25  },
  // -- STORAGE TIER -- 25 GbE NIC (storage I/O heavy)
  { id: 'r740xd', name: 'Dell PowerEdge R740xd', description: '2U storage server. Dual Xeon SP, 24x LFF drive bays + 2x NVMe. Perfect for NAS, backup, and S3-compatible storage.',                                       cost: 15000,  powerDraw: 0.75, coolingNeeded: 0.69, revenue: 13500, customers: 50, unlockLevel: 2, uSize: 2, contractCategory: 'colocation', trafficGbps: 3.0,   nicSpeedGbps: 25  },
  { id: 'r750xd', name: 'Dell PowerEdge R750xd', description: '2U NVMe storage server. Dual Xeon SP 4th gen, up to 24x NVMe U.2, 24 DIMM slots. High-IOPS block storage platform.',                                       cost: 20000,  powerDraw: 1.00, coolingNeeded: 0.92, revenue: 19500, customers: 60, unlockLevel: 3, uSize: 2, contractCategory: 'colocation', trafficGbps: 6.0,   nicSpeedGbps: 25  },
  // -- GPU / AI TIER -- 100 GbE NIC
  { id: 'r750xa', name: 'Dell PowerEdge R750xa', description: '2U GPU server. Dual Xeon SP, 4x NVIDIA A30/A10 GPUs, NVLink. ML training, inference, and rendering workloads.',                                             cost: 45000,  powerDraw: 2.50, coolingNeeded: 2.30, revenue: 52500, customers: 5,  unlockLevel: 4, uSize: 2, contractCategory: 'enterprise',  trafficGbps: 10.0,  nicSpeedGbps: 100 },
  { id: 'r760xa', name: 'Dell PowerEdge R760xa', description: '2U next-gen GPU server. Dual Xeon SP 4th gen, 4x NVIDIA H100 MIG-capable GPUs. Large-scale AI training and LLM inference.',                                 cost: 85000,  powerDraw: 4.00, coolingNeeded: 3.68, revenue: 105000, customers: 3,  unlockLevel: 5, uSize: 2, contractCategory: 'enterprise',  trafficGbps: 15.0,  nicSpeedGbps: 100 },
  // -- AI FLAGSHIP -- 400 GbE NIC
  { id: 'xe9680', name: 'Dell PowerEdge XE9680', description: '5U flagship AI server. Dual Xeon SP, 8x NVIDIA H100 SXM5 GPUs (NVLink 4.0), 8x NVMe. The highest-density AI compute unit available.',                      cost: 220000, powerDraw: 10.0, coolingNeeded: 9.20, revenue: 270000, customers: 2,  unlockLevel: 6, uSize: 5, contractCategory: 'enterprise',  trafficGbps: 50.0,  nicSpeedGbps: 400 },
];

// MPO patch panels — installed at U48, required before TOR switch or servers
export const MPO_PANEL_TYPES: MpoPatchPanelType[] = [
  {
    id: 'mpo-12',
    name: 'MPO-12 Patch Panel',
    description: '12-fiber LC duplex panel. Supports 1G/10G TOR uplinks. Entry-level racks.',
    cost: 500,
    fiberCount: 12,
    maxUplinkGbps: 10,
  },
  {
    id: 'mpo-24',
    name: 'MPO-24 Patch Panel',
    description: '24-fiber LC panel. Supports up to 100G uplinks. Standard for mid/high racks.',
    cost: 1200,
    fiberCount: 24,
    maxUplinkGbps: 100,
  },
  {
    id: 'mpo-48',
    name: 'MTP Trunk Panel (MPO-48)',
    description: '48-fiber MTP trunk panel. Required for 400G uplinks (GPU/AI racks).',
    cost: 2500,
    fiberCount: 48,
    maxUplinkGbps: 400,
  },
];

// Floor Horizontal Distribution Area (HDA) switches — one per floor, aggregates TOR uplinks
export const HDA_SWITCH_TYPES: HdaSwitchType[] = [
  {
    id: 'hda-40g',
    name: 'HDA Switch 40G',
    description: 'Cisco Catalyst 9300 48-port. 40G aggregate floor bandwidth. Suitable for floors with entry/mid-tier racks.',
    cost: 8000,
    bandwidthGbps: 40,
    powerDrawKW: 0.30,
  },
  {
    id: 'hda-100g',
    name: 'HDA Switch 100G',
    description: 'Arista 7050X 64-port. 100G aggregate. Standard for performance and mixed server floors.',
    cost: 18000,
    bandwidthGbps: 100,
    powerDrawKW: 0.50,
  },
  {
    id: 'hda-400g',
    name: 'HDA Switch 400G',
    description: 'Cisco Nexus 9336C-FX2. 400G aggregate. Required for GPU/AI floors or dense 100G TOR environments.',
    cost: 45000,
    bandwidthGbps: 400,
    powerDrawKW: 1.20,
  },
];

// Core switches — one per location, aggregates all floor HDA uplinks before the MMR
export const CORE_SWITCH_TYPES: CoreSwitchType[] = [
  {
    id: 'core-400g',
    name: 'Core Switch 400G',
    description: 'Cisco Nexus 93360YC-FX2. 400G switching fabric. Entry-level core. Connects all floors to the MMR.',
    cost: 25000,
    bandwidthGbps: 400,
    powerDrawKW: 1.00,
  },
  {
    id: 'core-1600g',
    name: 'Core Router 1.6T',
    description: 'Arista 7280R3A. 1.6 Tbps fabric. Suitable for multi-floor high-density datacenters.',
    cost: 65000,
    bandwidthGbps: 1600,
    powerDrawKW: 2.50,
  },
  {
    id: 'core-4000g',
    name: 'Core Switch 4T',
    description: 'Cisco Nexus 9508 modular chassis. 4 Tbps. Enterprise-grade, handles the largest configurations.',
    cost: 150000,
    bandwidthGbps: 4000,
    powerDrawKW: 6.00,
  },
];

// All datacenter location keys (must match the keys used in App.tsx setLocations).
export const EU_LOCATION_KEYS = [
  'rotterdam',  // Heerlen (NL)
  'suburban',   // Rotterdam (NL)
  'amsterdam',  // Amsterdam (NL)
  'industrial', // Frankfurt (DE)
  'london', 'paris', 'madrid', 'milan', 'stockholm', 'warsaw', 'bucharest', 'istanbul',
];
export const ALL_LOCATION_KEYS = [
  ...EU_LOCATION_KEYS,
  // Americas
  'losangeles', 'seattle', 'dallas', 'chicago', 'ashburn', 'newark',
  'saopaulo', 'santiago', 'buenosaires',
  // Africa / Middle East / Asia-Pacific
  'johannesburg', 'dubai', 'hongkong', 'tokyo', 'sydney',
];
const NL_LOCATION_KEYS = ['rotterdam', 'suburban', 'amsterdam'];

// Transit providers — ordered from the MMR, billed per Gbps per in-game month.
// availableLocations uses real LOCATION KEYS (not city names). Global/EU carriers
// are reachable at every datacenter; KPN is NL-only.
export const TRANSIT_PROVIDERS: TransitProvider[] = [
  {
    id: 'transit-kpn',
    name: 'KPN IP Transit',
    description: 'Dutch incumbent carrier. Excellent NL peering, lower international coverage.',
    region: 'NL',
    costPerGbpsMonth: 200,
    availableLocations: NL_LOCATION_KEYS,
    minCommitGbps: 1,
    maxCommitGbps: 100,
    color: 'text-blue-400',
  },
  {
    id: 'transit-telia',
    name: 'Telia Carrier',
    description: 'Tier-1 EU backbone. Strong European peering, competitive pricing.',
    region: 'EU',
    costPerGbpsMonth: 150,
    availableLocations: EU_LOCATION_KEYS,
    minCommitGbps: 1,
    maxCommitGbps: 400,
    color: 'text-purple-400',
  },
  {
    id: 'transit-cogent',
    name: 'Cogent Communications',
    description: 'Global Tier-1 at the lowest per-Gbps rate. Lower NL/DE peering quality.',
    region: 'Global',
    costPerGbpsMonth: 80,
    availableLocations: ALL_LOCATION_KEYS,
    minCommitGbps: 10,
    maxCommitGbps: 1000,
    color: 'text-green-400',
  },
  {
    id: 'transit-ntt',
    name: 'NTT Communications',
    description: 'Global Tier-1. Excellent Asia-Pacific routes, solid EU backbone.',
    region: 'Global',
    costPerGbpsMonth: 180,
    availableLocations: ALL_LOCATION_KEYS,
    minCommitGbps: 1,
    maxCommitGbps: 400,
    color: 'text-orange-400',
  },
  {
    id: 'transit-lumen',
    name: 'Lumen Technologies',
    description: 'Global Tier-1 (formerly CenturyLink). Competitive rates, strong NA/EU coverage.',
    region: 'Global',
    costPerGbpsMonth: 120,
    availableLocations: ALL_LOCATION_KEYS,
    minCommitGbps: 5,
    maxCommitGbps: 400,
    color: 'text-yellow-400',
  },
];

// Internet Exchange Points — flat monthly port fee, traffic is free (peering)
export const IXP_EXCHANGES: IxpExchange[] = [
  {
    id: 'ams-ix',
    name: 'AMS-IX Amsterdam',
    description: "World's largest internet exchange by connected ASNs. Essential for NL/EU peering and low-latency access to major content networks.",
    availableLocations: NL_LOCATION_KEYS,
    revenueBonus: 0.12,
    ports: [
      { speedGbps: 1,   monthlyCost: 300,  setupCost: 1000 },
      { speedGbps: 10,  monthlyCost: 1000, setupCost: 1000 },
      { speedGbps: 100, monthlyCost: 3000, setupCost: 1000 },
    ],
  },
  {
    id: 'de-cix',
    name: 'DE-CIX Frankfurt',
    description: 'Largest IX in the EU by peak traffic. Cross-connects to major cloud providers and CDN networks at Frankfurt.',
    availableLocations: ['industrial'],
    revenueBonus: 0.15,
    ports: [
      { speedGbps: 1,   monthlyCost: 280,  setupCost: 800 },
      { speedGbps: 10,  monthlyCost: 800,  setupCost: 800 },
      { speedGbps: 100, monthlyCost: 2800, setupCost: 800 },
    ],
  },
  {
    id: 'nl-ix',
    name: 'NL-ix Netherlands',
    description: 'Dutch neutral internet exchange. Cost-effective domestic peering, available at all NL locations.',
    availableLocations: NL_LOCATION_KEYS,
    revenueBonus: 0.10,
    ports: [
      { speedGbps: 1,   monthlyCost: 150,  setupCost: 500 },
      { speedGbps: 10,  monthlyCost: 500,  setupCost: 500 },
      { speedGbps: 100, monthlyCost: 1500, setupCost: 500 },
    ],
  },
  // ── Regional exchanges — one per remaining country so every DC can peer ──
  {
    id: 'linx',
    name: 'LINX London',
    description: 'London Internet Exchange — one of the largest IXes in Europe, gateway to UK and transatlantic networks.',
    availableLocations: ['london'],
    revenueBonus: 0.13,
    ports: [
      { speedGbps: 1,   monthlyCost: 300,  setupCost: 900 },
      { speedGbps: 10,  monthlyCost: 950,  setupCost: 900 },
      { speedGbps: 100, monthlyCost: 2900, setupCost: 900 },
    ],
  },
  {
    id: 'france-ix',
    name: 'France-IX Paris',
    description: "France's leading internet exchange. Strong access to French and southern-European content networks.",
    availableLocations: ['paris'],
    revenueBonus: 0.11,
    ports: [
      { speedGbps: 1,   monthlyCost: 270,  setupCost: 800 },
      { speedGbps: 10,  monthlyCost: 820,  setupCost: 800 },
      { speedGbps: 100, monthlyCost: 2600, setupCost: 800 },
    ],
  },
  {
    id: 'espanix',
    name: 'ESPANIX Madrid',
    description: 'Spain\'s main neutral exchange. Key peering point for Iberian and Latin-American-facing traffic.',
    availableLocations: ['madrid'],
    revenueBonus: 0.09,
    ports: [
      { speedGbps: 1,   monthlyCost: 220,  setupCost: 600 },
      { speedGbps: 10,  monthlyCost: 680,  setupCost: 600 },
      { speedGbps: 100, monthlyCost: 2100, setupCost: 600 },
    ],
  },
  {
    id: 'mix',
    name: 'MIX Milan',
    description: 'Milan Internet Exchange — the largest IX in Italy, anchoring southern-European connectivity.',
    availableLocations: ['milan'],
    revenueBonus: 0.10,
    ports: [
      { speedGbps: 1,   monthlyCost: 230,  setupCost: 650 },
      { speedGbps: 10,  monthlyCost: 700,  setupCost: 650 },
      { speedGbps: 100, monthlyCost: 2200, setupCost: 650 },
    ],
  },
  {
    id: 'netnod',
    name: 'Netnod Stockholm',
    description: 'Nordic neutral exchange. Excellent Scandinavian peering and low-latency Baltic routes.',
    availableLocations: ['stockholm'],
    revenueBonus: 0.10,
    ports: [
      { speedGbps: 1,   monthlyCost: 210,  setupCost: 600 },
      { speedGbps: 10,  monthlyCost: 660,  setupCost: 600 },
      { speedGbps: 100, monthlyCost: 2000, setupCost: 600 },
    ],
  },
  {
    id: 'plix',
    name: 'PLIX Warsaw',
    description: 'Polish Internet Exchange — the main peering hub for Poland and central-eastern Europe.',
    availableLocations: ['warsaw'],
    revenueBonus: 0.08,
    ports: [
      { speedGbps: 1,   monthlyCost: 180,  setupCost: 500 },
      { speedGbps: 10,  monthlyCost: 560,  setupCost: 500 },
      { speedGbps: 100, monthlyCost: 1700, setupCost: 500 },
    ],
  },
  {
    id: 'interlan',
    name: 'InterLAN Bucharest',
    description: 'Romanian neutral exchange. Cost-effective peering for south-eastern Europe.',
    availableLocations: ['bucharest'],
    revenueBonus: 0.07,
    ports: [
      { speedGbps: 1,   monthlyCost: 150,  setupCost: 450 },
      { speedGbps: 10,  monthlyCost: 480,  setupCost: 450 },
      { speedGbps: 100, monthlyCost: 1500, setupCost: 450 },
    ],
  },
  {
    id: 'ist-ix',
    name: 'IST-IX Istanbul',
    description: 'Istanbul internet exchange — bridges European and Middle-East/Asia-facing traffic.',
    availableLocations: ['istanbul'],
    revenueBonus: 0.09,
    ports: [
      { speedGbps: 1,   monthlyCost: 190,  setupCost: 550 },
      { speedGbps: 10,  monthlyCost: 600,  setupCost: 550 },
      { speedGbps: 100, monthlyCost: 1900, setupCost: 550 },
    ],
  },
  // ── Global exchanges — one per non-European datacenter ──
  {
    id: 'any2-la',
    name: 'Any2West Los Angeles',
    description: 'One Wilshire — the primary US West Coast exchange, gateway to trans-Pacific cables.',
    availableLocations: ['losangeles'],
    revenueBonus: 0.11,
    ports: [
      { speedGbps: 1,   monthlyCost: 280,  setupCost: 850 },
      { speedGbps: 10,  monthlyCost: 850,  setupCost: 850 },
      { speedGbps: 100, monthlyCost: 2700, setupCost: 850 },
    ],
  },
  {
    id: 'six-seattle',
    name: 'SIX Seattle',
    description: 'Seattle Internet Exchange — non-profit IX with strong Pacific Northwest and Asia-facing peering.',
    availableLocations: ['seattle'],
    revenueBonus: 0.10,
    ports: [
      { speedGbps: 1,   monthlyCost: 200,  setupCost: 600 },
      { speedGbps: 10,  monthlyCost: 620,  setupCost: 600 },
      { speedGbps: 100, monthlyCost: 2000, setupCost: 600 },
    ],
  },
  {
    id: 'decix-dallas',
    name: 'DE-CIX Dallas',
    description: 'Interconnection hub for the southern US — strong access to Texas carrier networks.',
    availableLocations: ['dallas'],
    revenueBonus: 0.09,
    ports: [
      { speedGbps: 1,   monthlyCost: 230,  setupCost: 700 },
      { speedGbps: 10,  monthlyCost: 700,  setupCost: 700 },
      { speedGbps: 100, monthlyCost: 2200, setupCost: 700 },
    ],
  },
  {
    id: 'unitedix-chicago',
    name: 'United IX Chicago',
    description: 'Chicago exchange at the crossroads of US east-west fiber routes and financial networks.',
    availableLocations: ['chicago'],
    revenueBonus: 0.10,
    ports: [
      { speedGbps: 1,   monthlyCost: 250,  setupCost: 750 },
      { speedGbps: 10,  monthlyCost: 780,  setupCost: 750 },
      { speedGbps: 100, monthlyCost: 2400, setupCost: 750 },
    ],
  },
  {
    id: 'equinix-ashburn',
    name: 'Equinix IX Ashburn',
    description: 'The densest interconnection campus on earth — Ashburn is the heart of the global internet.',
    availableLocations: ['ashburn'],
    revenueBonus: 0.15,
    ports: [
      { speedGbps: 1,   monthlyCost: 320,  setupCost: 1000 },
      { speedGbps: 10,  monthlyCost: 1000, setupCost: 1000 },
      { speedGbps: 100, monthlyCost: 3100, setupCost: 1000 },
    ],
  },
  {
    id: 'decix-ny',
    name: 'DE-CIX New York',
    description: 'Major New York metro exchange — dense east-coast peering and transatlantic cable access.',
    availableLocations: ['newark'],
    revenueBonus: 0.12,
    ports: [
      { speedGbps: 1,   monthlyCost: 300,  setupCost: 900 },
      { speedGbps: 10,  monthlyCost: 900,  setupCost: 900 },
      { speedGbps: 100, monthlyCost: 2800, setupCost: 900 },
    ],
  },
  {
    id: 'ixbr-sp',
    name: 'IX.br São Paulo',
    description: 'The largest IX in the world by connected networks — the peering heart of Latin America.',
    availableLocations: ['saopaulo'],
    revenueBonus: 0.12,
    ports: [
      { speedGbps: 1,   monthlyCost: 180,  setupCost: 550 },
      { speedGbps: 10,  monthlyCost: 560,  setupCost: 550 },
      { speedGbps: 100, monthlyCost: 1800, setupCost: 550 },
    ],
  },
  {
    id: 'pit-chile',
    name: 'PIT Chile Santiago',
    description: "Chile's neutral exchange — key landing point for Pacific submarine cables into South America.",
    availableLocations: ['santiago'],
    revenueBonus: 0.08,
    ports: [
      { speedGbps: 1,   monthlyCost: 160,  setupCost: 500 },
      { speedGbps: 10,  monthlyCost: 500,  setupCost: 500 },
      { speedGbps: 100, monthlyCost: 1600, setupCost: 500 },
    ],
  },
  {
    id: 'cabase-ba',
    name: 'CABASE IXP Buenos Aires',
    description: 'Argentine national exchange federation — cost-effective peering for the southern cone.',
    availableLocations: ['buenosaires'],
    revenueBonus: 0.08,
    ports: [
      { speedGbps: 1,   monthlyCost: 150,  setupCost: 450 },
      { speedGbps: 10,  monthlyCost: 480,  setupCost: 450 },
      { speedGbps: 100, monthlyCost: 1500, setupCost: 450 },
    ],
  },
  {
    id: 'napafrica',
    name: 'NAPAfrica Johannesburg',
    description: "Africa's largest internet exchange — free peering model, anchors the continent's traffic.",
    availableLocations: ['johannesburg'],
    revenueBonus: 0.11,
    ports: [
      { speedGbps: 1,   monthlyCost: 140,  setupCost: 450 },
      { speedGbps: 10,  monthlyCost: 450,  setupCost: 450 },
      { speedGbps: 100, monthlyCost: 1400, setupCost: 450 },
    ],
  },
  {
    id: 'uae-ix',
    name: 'UAE-IX Dubai',
    description: 'Middle East carrier-neutral exchange — regional hub between Europe, Africa and Asia.',
    availableLocations: ['dubai'],
    revenueBonus: 0.10,
    ports: [
      { speedGbps: 1,   monthlyCost: 260,  setupCost: 800 },
      { speedGbps: 10,  monthlyCost: 800,  setupCost: 800 },
      { speedGbps: 100, monthlyCost: 2500, setupCost: 800 },
    ],
  },
  {
    id: 'hkix',
    name: 'HKIX Hong Kong',
    description: "Hong Kong Internet Exchange — the region's dominant hub for intra-Asia peering.",
    availableLocations: ['hongkong'],
    revenueBonus: 0.12,
    ports: [
      { speedGbps: 1,   monthlyCost: 290,  setupCost: 850 },
      { speedGbps: 10,  monthlyCost: 880,  setupCost: 850 },
      { speedGbps: 100, monthlyCost: 2700, setupCost: 850 },
    ],
  },
  {
    id: 'jpnap',
    name: 'JPNAP Tokyo',
    description: 'One of the largest exchanges in Asia — premium Japanese and trans-Pacific connectivity.',
    availableLocations: ['tokyo'],
    revenueBonus: 0.13,
    ports: [
      { speedGbps: 1,   monthlyCost: 310,  setupCost: 950 },
      { speedGbps: 10,  monthlyCost: 950,  setupCost: 950 },
      { speedGbps: 100, monthlyCost: 3000, setupCost: 950 },
    ],
  },
  {
    id: 'ix-au',
    name: 'IX Australia Sydney',
    description: 'Australian neutral exchange — gateway for Oceania traffic and trans-Pacific routes.',
    availableLocations: ['sydney'],
    revenueBonus: 0.10,
    ports: [
      { speedGbps: 1,   monthlyCost: 240,  setupCost: 700 },
      { speedGbps: 10,  monthlyCost: 750,  setupCost: 700 },
      { speedGbps: 100, monthlyCost: 2300, setupCost: 700 },
    ],
  },
];

// NaWas DDoS scrubbing tiers (NBIP — Nationale Wasstraat)
// Requires: ASN + PI IP space + MMR. Reroutes attack traffic via BGP anycast to scrubbing centres.
export const NAWAS_TIERS: NawasTier[] = [
  {
    id: 'nawas-basic',
    name: 'NaWas Basic',
    description: 'Up to 10 Gbps of DDoS scrubbing. BGP reroute in ~2 min. Good for small attacks.',
    scrubCapacityGbps: 10,
    monthlyCost: 500,
    setupCost: 1000,
  },
  {
    id: 'nawas-standard',
    name: 'NaWas Standard',
    description: 'Up to 50 Gbps scrubbing. Covers most volumetric attacks against mid-tier DCs.',
    scrubCapacityGbps: 50,
    monthlyCost: 1500,
    setupCost: 2500,
  },
  {
    id: 'nawas-premium',
    name: 'NaWas Premium',
    description: '200 Gbps scrubbing. Full mitigation for even large-scale attacks. Industry-grade.',
    scrubCapacityGbps: 200,
    monthlyCost: 5000,
    setupCost: 5000,
  },
];

// Top-of-Rack switch types — required before servers can be installed in a rack
export const TOR_SWITCH_TYPES: TORSwitchType[] = [
  {
    id: 'tor-24p-10g',
    name: '24-Port 10GbE TOR',
    description: 'Basic 24-port 10GbE switch. 2×10G uplink. Requires MPO-12 panel.',
    cost: 2500,
    portCount: 24,
    uplinkBandwidthGbps: 10,
    powerDrawKW: 0.15,
  },
  {
    id: 'tor-48p-10g',
    name: '48-Port 10GbE TOR',
    description: 'Standard 48-port 10GbE switch. 2×10G uplink. Requires MPO-12 panel.',
    cost: 4500,
    portCount: 48,
    uplinkBandwidthGbps: 10,
    powerDrawKW: 0.20,
  },
  {
    id: 'tor-48p-25g',
    name: '48-Port 25GbE TOR',
    description: '25GbE downlinks for high-throughput compute and storage servers.',
    cost: 9000,
    portCount: 48,
    uplinkBandwidthGbps: 100,
    powerDrawKW: 0.30,
  },
  {
    id: 'tor-48p-100g',
    name: '48-Port 100GbE TOR',
    description: 'High-density 100GbE for GPU clusters and AI workloads.',
    cost: 22000,
    portCount: 48,
    uplinkBandwidthGbps: 400,
    powerDrawKW: 0.45,
  },
];

// Out-of-band management switches (U44) — Juniper EX series.
// Provide iDRAC/iLO connectivity so servers in the rack can be managed and repaired
// remotely. Without one, technicians can't remotely service that rack (no auto-repair,
// and manual repairs take longer because they require on-site dispatch).
export const MGMT_SWITCH_TYPES: ManagementSwitchType[] = [
  {
    id: 'ex2200-24',
    name: 'Juniper EX2200-24',
    description: '24-port 1GbE management switch. Out-of-band iDRAC/iLO access for up to 24 servers.',
    cost: 1800,
    managedPorts: 24,
    powerDrawKW: 0.05,
  },
  {
    id: 'ex3400-48',
    name: 'Juniper EX3400-48',
    description: '48-port 1GbE management switch with redundant PSUs. OOB management for a full rack.',
    cost: 4200,
    managedPorts: 48,
    powerDrawKW: 0.09,
  },
  {
    id: 'ex4300-48',
    name: 'Juniper EX4300-48',
    description: 'Enterprise 48-port management switch with Virtual Chassis. Fastest remote console + power control.',
    cost: 7500,
    managedPorts: 48,
    powerDrawKW: 0.12,
  },
];

// Server unlock thresholds: unlockLevel → minimum total servers required
export const SERVER_UNLOCK_THRESHOLDS: Record<number, number> = {
  0: 0,   // always available
  1: 3,   // need 3 total servers
  2: 8,
  3: 15,
  4: 25,
  5: 40,
  6: 60,
};

// Monthly maintenance cost as a fraction of server purchase price
export const SERVER_MAINTENANCE_RATE = 0.015;

// Diesel generator levels (index 0 = none). Provides backup power during outages.
export const GENERATOR_LEVELS = [
  { level: 0, runtimeHours: 0,  fuelCostPerHour: 0,   powerOutputKW: 0,    cost: 0 },
  { level: 1, runtimeHours: 8,  fuelCostPerHour: 45,  powerOutputKW: 200,  cost: 25000 },
  { level: 2, runtimeHours: 24, fuelCostPerHour: 80,  powerOutputKW: 500,  cost: 75000 },
  { level: 3, runtimeHours: 72, fuelCostPerHour: 120, powerOutputKW: 1000, cost: 180000 },
];

// Cost to refill one hour of generator fuel capacity
export const GENERATOR_FUEL_REFILL_COST_PER_HOUR = 15;

// Hardware failure tuning
export const SERVER_FAILURE_PROBABILITY_PER_TICK = 0.00005; // per operational server, per failure-check tick
export const SERVER_REPAIR_BASE_COST_MULTIPLIER = 0.05;     // repair costs 5% of server purchase price
export const SERVER_REPAIR_TICKS = 300;                     // ~5 in-game minutes (60 ticks = 1 minute)

// Facility safety — equipment placed in the Site Layout Editor yields per-location bonuses.
// Counts of each item type are converted to capped reductions on the relevant systems.
export const FACILITY_SAFETY = {
  extinguisherFirePerUnit: 0.08,  // each extinguisher: −8% cooling-failure event probability
  extinguisherFireCap: 0.48,      // capped at −48% (≈6 units)
  accessControlPerUnit: 0.08,     // each tourniquet door: −8% hardware-recall (tampering) probability
  accessControlCap: 0.40,         // capped at −40% (5 units)
  cabinetRepairPerUnit: 0.05,     // each spare-parts cabinet: −5% server repair time
  cabinetRepairCap: 0.30,         // capped at −30% (6 units), stacks with technicians
  binFailurePerUnit: 0.03,        // each bin (cleanliness): −3% baseline hardware failure probability
  binFailureCap: 0.24,            // capped at −24% (8 units)
};

// Customer contract categories — determine customer profile and SLA exposure per server
export const CONTRACT_TYPES: ContractType[] = [
  {
    id: 'webhosting',
    name: 'Web Hosting',
    shortLabel: 'Web',
    customersPerServer: 50,
    revenuePerCustomer: 3,
    slaDowntimePenaltyPct: 0.10,
    color: 'text-sky-400',
    description: 'Many small shared-hosting customers. Low revenue each, lenient SLA.',
  },
  {
    id: 'colocation',
    name: 'Colocation',
    shortLabel: 'Colo',
    customersPerServer: 10,
    revenuePerCustomer: 60,
    slaDowntimePenaltyPct: 0.25,
    color: 'text-amber-400',
    description: 'Business customers renting rack space. Moderate revenue and SLA exposure.',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    shortLabel: 'Enterprise',
    customersPerServer: 2,
    revenuePerCustomer: 500,
    slaDowntimePenaltyPct: 0.60,
    color: 'text-fuchsia-400',
    description: 'Few high-value clients with strict uptime guarantees. Heavy SLA penalties on downtime.',
  },
];

// Power grid suppliers
export const POWER_SUPPLIERS: PowerSupplier[] = [
  {
    id: 'cityGrid',
    name: 'City Power Grid',
    costPerKWh: 0.15,
    reliability: 0.95,
    greenEnergy: 0.2,
    maxOutput: 1000,
    tariffSchedule: [
      { startHour: 7, endHour: 21, rate: 0.18 },
      { startHour: 21, endHour: 7, rate: 0.12 }
    ]
  },
  {
    id: 'greenEnergy',
    name: 'Green Energy Co.',
    costPerKWh: 0.18,
    reliability: 0.92,
    greenEnergy: 0.9,
    maxOutput: 800,
    tariffSchedule: [
      { startHour: 9, endHour: 17, rate: 0.17 },
      { startHour: 17, endHour: 9, rate: 0.20 }
    ]
  },
  {
    id: 'industrialPower',
    name: 'Industrial Power Inc.',
    costPerKWh: 0.12,
    reliability: 0.98,
    greenEnergy: 0.05,
    maxOutput: 2000,
    tariffSchedule: [
      { startHour: 8, endHour: 20, rate: 0.13 },
      { startHour: 20, endHour: 8, rate: 0.11 }
    ]
  }
];

// Power infrastructure levels.
// Efficiencies are realistic: an MV transformer is ~97–99% efficient, LV switchgear
// ~99%, and an online double-conversion UPS ~94–97%. The small remaining losses are
// electrical; the bulk of a datacenter's overhead (PUE) lives in the COOLING chain.
export const TRANSFORMER_LEVELS = [
  { level: 0, capacityKW: 0, efficiency: 0, cost: 0 },
  { level: 1, capacityKW: 100, efficiency: 0.970, cost: 15000 },
  { level: 2, capacityKW: 250, efficiency: 0.978, cost: 50000 },
  { level: 3, capacityKW: 500, efficiency: 0.985, cost: 100000 },
  { level: 4, capacityKW: 1000, efficiency: 0.990, cost: 200000 },
  { level: 5, capacityKW: 2000, efficiency: 0.995, cost: 400000 }
];

// "Powerhouse" in the data model = the LV Switchgear / Main Distribution Board (MDB).
export const POWERHOUSE_LEVELS = [
  { level: 0, capacityKW: 0, efficiency: 0, cost: 0 },
  { level: 1, capacityKW: 200, efficiency: 0.980, cost: 20000 },
  { level: 2, capacityKW: 1000, efficiency: 0.985, cost: 150000 },
  { level: 3, capacityKW: 2000, efficiency: 0.990, cost: 200000 },
  { level: 4, capacityKW: 5000, efficiency: 0.993, cost: 500000 },
  { level: 5, capacityKW: 10000, efficiency: 0.996, cost: 1000000 }
];

export const UPS_LEVELS = [
  { level: 0, capacityKWh: 0, maxOutputKW: 0, efficiency: 0, cost: 0 },
  { level: 1, capacityKWh: 50, maxOutputKW: 100, efficiency: 0.940, cost: 10000 },
  { level: 2, capacityKWh: 100, maxOutputKW: 200, efficiency: 0.950, cost: 50000 },
  { level: 3, capacityKWh: 250, maxOutputKW: 500, efficiency: 0.960, cost: 125000 },
  { level: 4, capacityKWh: 500, maxOutputKW: 1000, efficiency: 0.970, cost: 250000 },
  { level: 5, capacityKWh: 1000, maxOutputKW: 2000, efficiency: 0.975, cost: 500000 }
];

// Power grid data with proper typing — one grid per country/region
export const POWER_GRIDS: PowerGrids = {
  'netherlands-grid': {
    id: 'netherlands-grid',
    locationName: 'Netherlands Power Grid (Tennet NL)',
    totalCapacityGW: 2.5,
    currentLoadGW: 0.8,
    suppliers: [
      {
        id: 'nl-ecopower',
        name: 'EcoPower Nederland',
        costPerKWh: 0.15,
        reliability: 0.99,
        greenEnergy: 0.85,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 17, rate: 0.14 },
          { startHour: 17, endHour: 9, rate: 0.16 }
        ]
      },
      {
        id: 'nl-standard',
        name: 'Eneco Business',
        costPerKWh: 0.12,
        reliability: 0.995,
        greenEnergy: 0.25,
        maxOutput: 10000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.13 },
          { startHour: 21, endHour: 7, rate: 0.11 }
        ]
      },
      {
        id: 'nl-industrial',
        name: 'Vattenfall Industrie',
        costPerKWh: 0.10,
        reliability: 0.98,
        greenEnergy: 0.05,
        maxOutput: 20000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.105 },
          { startHour: 22, endHour: 6, rate: 0.095 }
        ]
      }
    ]
  },
  'germany-grid': {
    id: 'germany-grid',
    locationName: 'German Power Grid (Tennet DE / Amprion)',
    totalCapacityGW: 8.5,
    currentLoadGW: 3.2,
    suppliers: [
      {
        id: 'de-enbw',
        name: 'EnBW Energie',
        costPerKWh: 0.16,
        reliability: 0.99,
        greenEnergy: 0.55,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 8, endHour: 20, rate: 0.17 },
          { startHour: 20, endHour: 8, rate: 0.14 }
        ]
      },
      {
        id: 'de-rwe',
        name: 'RWE Power GmbH',
        costPerKWh: 0.13,
        reliability: 0.997,
        greenEnergy: 0.30,
        maxOutput: 15000,
        tariffSchedule: [
          { startHour: 7, endHour: 22, rate: 0.14 },
          { startHour: 22, endHour: 7, rate: 0.11 }
        ]
      },
      {
        id: 'de-industrial',
        name: 'E.ON Industrie',
        costPerKWh: 0.11,
        reliability: 0.985,
        greenEnergy: 0.10,
        maxOutput: 25000,
        tariffSchedule: [
          { startHour: 6, endHour: 21, rate: 0.115 },
          { startHour: 21, endHour: 6, rate: 0.10 }
        ]
      }
    ]
  },
  'uk-grid': {
    id: 'uk-grid',
    locationName: 'UK National Grid (National Grid ESO)',
    totalCapacityGW: 6.0,
    currentLoadGW: 2.1,
    suppliers: [
      {
        id: 'uk-octopus',
        name: 'Octopus Energy B2B',
        costPerKWh: 0.17,
        reliability: 0.99,
        greenEnergy: 0.75,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 17, rate: 0.16 },
          { startHour: 17, endHour: 9, rate: 0.18 }
        ]
      },
      {
        id: 'uk-british-gas',
        name: 'British Gas Business',
        costPerKWh: 0.14,
        reliability: 0.993,
        greenEnergy: 0.35,
        maxOutput: 12000,
        tariffSchedule: [
          { startHour: 7, endHour: 23, rate: 0.15 },
          { startHour: 23, endHour: 7, rate: 0.12 }
        ]
      },
      {
        id: 'uk-edf',
        name: 'EDF Energy Industrial',
        costPerKWh: 0.12,
        reliability: 0.99,
        greenEnergy: 0.20,
        maxOutput: 20000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.13 },
          { startHour: 22, endHour: 6, rate: 0.11 }
        ]
      }
    ]
  },
  'france-grid': {
    id: 'france-grid',
    locationName: 'French Power Grid (RTE)',
    totalCapacityGW: 7.0,
    currentLoadGW: 2.5,
    suppliers: [
      {
        id: 'fr-edf-vert',
        name: 'EDF Vert',
        costPerKWh: 0.13,
        reliability: 0.995,
        greenEnergy: 0.90,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 19, rate: 0.12 },
          { startHour: 19, endHour: 9, rate: 0.14 }
        ]
      },
      {
        id: 'fr-engie',
        name: 'Engie Business',
        costPerKWh: 0.11,
        reliability: 0.996,
        greenEnergy: 0.50,
        maxOutput: 12000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.115 },
          { startHour: 21, endHour: 7, rate: 0.10 }
        ]
      },
      {
        id: 'fr-direct',
        name: 'Direct Énergie Industrie',
        costPerKWh: 0.09,
        reliability: 0.992,
        greenEnergy: 0.15,
        maxOutput: 25000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.095 },
          { startHour: 22, endHour: 6, rate: 0.085 }
        ]
      }
    ]
  },
  'spain-grid': {
    id: 'spain-grid',
    locationName: 'Spanish Power Grid (REE)',
    totalCapacityGW: 4.5,
    currentLoadGW: 1.6,
    suppliers: [
      {
        id: 'es-iberdrola',
        name: 'Iberdrola Renovables',
        costPerKWh: 0.14,
        reliability: 0.99,
        greenEnergy: 0.80,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 10, endHour: 20, rate: 0.13 },
          { startHour: 20, endHour: 10, rate: 0.15 }
        ]
      },
      {
        id: 'es-endesa',
        name: 'Endesa Empresas',
        costPerKWh: 0.11,
        reliability: 0.993,
        greenEnergy: 0.35,
        maxOutput: 12000,
        tariffSchedule: [
          { startHour: 8, endHour: 22, rate: 0.12 },
          { startHour: 22, endHour: 8, rate: 0.10 }
        ]
      },
      {
        id: 'es-naturgy',
        name: 'Naturgy Industrial',
        costPerKWh: 0.095,
        reliability: 0.985,
        greenEnergy: 0.10,
        maxOutput: 20000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.10 },
          { startHour: 21, endHour: 7, rate: 0.09 }
        ]
      }
    ]
  },
  'italy-grid': {
    id: 'italy-grid',
    locationName: 'Italian Power Grid (Terna)',
    totalCapacityGW: 4.0,
    currentLoadGW: 1.5,
    suppliers: [
      {
        id: 'it-enel-green',
        name: 'Enel Green Power',
        costPerKWh: 0.15,
        reliability: 0.99,
        greenEnergy: 0.65,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 19, rate: 0.14 },
          { startHour: 19, endHour: 9, rate: 0.16 }
        ]
      },
      {
        id: 'it-a2a',
        name: 'A2A Energia Business',
        costPerKWh: 0.13,
        reliability: 0.990,
        greenEnergy: 0.30,
        maxOutput: 10000,
        tariffSchedule: [
          { startHour: 7, endHour: 22, rate: 0.135 },
          { startHour: 22, endHour: 7, rate: 0.12 }
        ]
      },
      {
        id: 'it-eni',
        name: 'Eni Plenitude Industria',
        costPerKWh: 0.11,
        reliability: 0.987,
        greenEnergy: 0.10,
        maxOutput: 18000,
        tariffSchedule: [
          { startHour: 6, endHour: 21, rate: 0.115 },
          { startHour: 21, endHour: 6, rate: 0.10 }
        ]
      }
    ]
  },
  'sweden-grid': {
    id: 'sweden-grid',
    locationName: 'Swedish Power Grid (Svenska Kraftnät)',
    totalCapacityGW: 5.0,
    currentLoadGW: 1.2,
    suppliers: [
      {
        id: 'se-vattenfall',
        name: 'Vattenfall Norden',
        costPerKWh: 0.08,
        reliability: 0.999,
        greenEnergy: 0.95,
        maxOutput: 8000,
        tariffSchedule: [
          { startHour: 8, endHour: 18, rate: 0.07 },
          { startHour: 18, endHour: 8, rate: 0.09 }
        ]
      },
      {
        id: 'se-fortum',
        name: 'Fortum Business SE',
        costPerKWh: 0.07,
        reliability: 0.998,
        greenEnergy: 0.85,
        maxOutput: 15000,
        tariffSchedule: [
          { startHour: 7, endHour: 20, rate: 0.075 },
          { startHour: 20, endHour: 7, rate: 0.065 }
        ]
      },
      {
        id: 'se-statkraft',
        name: 'Statkraft Industrial',
        costPerKWh: 0.06,
        reliability: 0.997,
        greenEnergy: 0.98,
        maxOutput: 30000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.065 },
          { startHour: 22, endHour: 6, rate: 0.055 }
        ]
      }
    ]
  },
  'poland-grid': {
    id: 'poland-grid',
    locationName: 'Polish Power Grid (PSE)',
    totalCapacityGW: 3.5,
    currentLoadGW: 1.4,
    suppliers: [
      {
        id: 'pl-pge-green',
        name: 'PGE Energia Odnawialna',
        costPerKWh: 0.13,
        reliability: 0.990,
        greenEnergy: 0.45,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 8, endHour: 20, rate: 0.12 },
          { startHour: 20, endHour: 8, rate: 0.14 }
        ]
      },
      {
        id: 'pl-enea',
        name: 'Enea Operator',
        costPerKWh: 0.105,
        reliability: 0.992,
        greenEnergy: 0.20,
        maxOutput: 10000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.11 },
          { startHour: 21, endHour: 7, rate: 0.09 }
        ]
      },
      {
        id: 'pl-tauron',
        name: 'Tauron Przemysł',
        costPerKWh: 0.09,
        reliability: 0.985,
        greenEnergy: 0.08,
        maxOutput: 20000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.095 },
          { startHour: 22, endHour: 6, rate: 0.085 }
        ]
      }
    ]
  },
  'romania-grid': {
    id: 'romania-grid',
    locationName: 'Romanian Power Grid (Transelectrica)',
    totalCapacityGW: 2.5,
    currentLoadGW: 0.9,
    suppliers: [
      {
        id: 'ro-enel-ro',
        name: 'Enel Energie România',
        costPerKWh: 0.12,
        reliability: 0.988,
        greenEnergy: 0.42,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 21, rate: 0.11 },
          { startHour: 21, endHour: 9, rate: 0.13 }
        ]
      },
      {
        id: 'ro-electrica',
        name: 'Electrica Furnizare',
        costPerKWh: 0.095,
        reliability: 0.985,
        greenEnergy: 0.18,
        maxOutput: 10000,
        tariffSchedule: [
          { startHour: 7, endHour: 22, rate: 0.10 },
          { startHour: 22, endHour: 7, rate: 0.09 }
        ]
      },
      {
        id: 'ro-cez',
        name: 'CEZ Industrie RO',
        costPerKWh: 0.08,
        reliability: 0.982,
        greenEnergy: 0.07,
        maxOutput: 18000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.085 },
          { startHour: 22, endHour: 6, rate: 0.075 }
        ]
      }
    ]
  },
  'turkey-grid': {
    id: 'turkey-grid',
    locationName: 'Turkish Power Grid (TEİAŞ)',
    totalCapacityGW: 5.5,
    currentLoadGW: 2.0,
    suppliers: [
      {
        id: 'tr-enerjisa',
        name: 'Enerjisa Enerji',
        costPerKWh: 0.13,
        reliability: 0.987,
        greenEnergy: 0.35,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 8, endHour: 22, rate: 0.14 },
          { startHour: 22, endHour: 8, rate: 0.12 }
        ]
      },
      {
        id: 'tr-akenerji',
        name: 'Akenerji Elektrik',
        costPerKWh: 0.105,
        reliability: 0.985,
        greenEnergy: 0.20,
        maxOutput: 12000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.11 },
          { startHour: 21, endHour: 7, rate: 0.10 }
        ]
      },
      {
        id: 'tr-zorlu',
        name: 'Zorlu Enerji Toptan',
        costPerKWh: 0.09,
        reliability: 0.983,
        greenEnergy: 0.08,
        maxOutput: 22000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.095 },
          { startHour: 22, endHour: 6, rate: 0.085 }
        ]
      }
    ]
  },
  'us-grid': {
    id: 'us-grid',
    locationName: 'US Power Grid (PJM / ERCOT / CAISO)',
    totalCapacityGW: 25.0,
    currentLoadGW: 11.0,
    suppliers: [
      {
        id: 'us-constellation',
        name: 'Constellation Clean Energy',
        costPerKWh: 0.13,
        reliability: 0.99,
        greenEnergy: 0.80,
        maxOutput: 8000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.12 },
          { startHour: 18, endHour: 9, rate: 0.14 }
        ]
      },
      {
        id: 'us-nrg',
        name: 'NRG Business',
        costPerKWh: 0.10,
        reliability: 0.994,
        greenEnergy: 0.30,
        maxOutput: 18000,
        tariffSchedule: [
          { startHour: 7, endHour: 22, rate: 0.11 },
          { startHour: 22, endHour: 7, rate: 0.09 }
        ]
      },
      {
        id: 'us-vistra',
        name: 'Vistra Wholesale',
        costPerKWh: 0.08,
        reliability: 0.985,
        greenEnergy: 0.12,
        maxOutput: 30000,
        tariffSchedule: [
          { startHour: 6, endHour: 21, rate: 0.085 },
          { startHour: 21, endHour: 6, rate: 0.075 }
        ]
      }
    ]
  },
  'brazil-grid': {
    id: 'brazil-grid',
    locationName: 'Brazilian Power Grid (ONS)',
    totalCapacityGW: 9.0,
    currentLoadGW: 3.5,
    suppliers: [
      {
        id: 'br-engie',
        name: 'Engie Brasil',
        costPerKWh: 0.12,
        reliability: 0.985,
        greenEnergy: 0.85,
        maxOutput: 6000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.115 },
          { startHour: 18, endHour: 9, rate: 0.125 }
        ]
      },
      {
        id: 'br-eletrobras',
        name: 'Eletrobras Corporativo',
        costPerKWh: 0.10,
        reliability: 0.98,
        greenEnergy: 0.70,
        maxOutput: 15000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.105 },
          { startHour: 21, endHour: 7, rate: 0.09 }
        ]
      },
      {
        id: 'br-cemig',
        name: 'CEMIG Atacado',
        costPerKWh: 0.085,
        reliability: 0.975,
        greenEnergy: 0.55,
        maxOutput: 22000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.09 },
          { startHour: 22, endHour: 6, rate: 0.08 }
        ]
      }
    ]
  },
  'chile-grid': {
    id: 'chile-grid',
    locationName: 'Chilean Power Grid (SEN)',
    totalCapacityGW: 3.0,
    currentLoadGW: 1.1,
    suppliers: [
      {
        id: 'cl-colbun',
        name: 'Colbún Energía',
        costPerKWh: 0.115,
        reliability: 0.982,
        greenEnergy: 0.75,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.11 },
          { startHour: 18, endHour: 9, rate: 0.12 }
        ]
      },
      {
        id: 'cl-enel',
        name: 'Enel Chile Empresas',
        costPerKWh: 0.10,
        reliability: 0.985,
        greenEnergy: 0.60,
        maxOutput: 10000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.105 },
          { startHour: 21, endHour: 7, rate: 0.09 }
        ]
      },
      {
        id: 'cl-aesandes',
        name: 'AES Andes Industrial',
        costPerKWh: 0.09,
        reliability: 0.978,
        greenEnergy: 0.40,
        maxOutput: 16000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.095 },
          { startHour: 22, endHour: 6, rate: 0.085 }
        ]
      }
    ]
  },
  'argentina-grid': {
    id: 'argentina-grid',
    locationName: 'Argentine Power Grid (SADI)',
    totalCapacityGW: 4.0,
    currentLoadGW: 1.6,
    suppliers: [
      {
        id: 'ar-genneia',
        name: 'Genneia Renovables',
        costPerKWh: 0.11,
        reliability: 0.975,
        greenEnergy: 0.80,
        maxOutput: 4000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.105 },
          { startHour: 18, endHour: 9, rate: 0.115 }
        ]
      },
      {
        id: 'ar-pampa',
        name: 'Pampa Energía',
        costPerKWh: 0.095,
        reliability: 0.97,
        greenEnergy: 0.30,
        maxOutput: 12000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.10 },
          { startHour: 21, endHour: 7, rate: 0.085 }
        ]
      },
      {
        id: 'ar-central-puerto',
        name: 'Central Puerto Mayorista',
        costPerKWh: 0.08,
        reliability: 0.965,
        greenEnergy: 0.15,
        maxOutput: 18000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.085 },
          { startHour: 22, endHour: 6, rate: 0.075 }
        ]
      }
    ]
  },
  'southafrica-grid': {
    id: 'southafrica-grid',
    locationName: 'South African Power Grid (Eskom)',
    totalCapacityGW: 4.5,
    currentLoadGW: 2.4,
    suppliers: [
      {
        id: 'za-seriti',
        name: 'Seriti Green',
        costPerKWh: 0.13,
        reliability: 0.955,
        greenEnergy: 0.75,
        maxOutput: 4000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.125 },
          { startHour: 18, endHour: 9, rate: 0.135 }
        ]
      },
      {
        id: 'za-eskom',
        name: 'Eskom Megaflex',
        costPerKWh: 0.10,
        reliability: 0.94,   // load-shedding risk
        greenEnergy: 0.12,
        maxOutput: 15000,
        tariffSchedule: [
          { startHour: 7, endHour: 21, rate: 0.11 },
          { startHour: 21, endHour: 7, rate: 0.09 }
        ]
      },
      {
        id: 'za-citypower',
        name: 'City Power Johannesburg',
        costPerKWh: 0.115,
        reliability: 0.95,
        greenEnergy: 0.10,
        maxOutput: 10000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.12 },
          { startHour: 22, endHour: 6, rate: 0.10 }
        ]
      }
    ]
  },
  'uae-grid': {
    id: 'uae-grid',
    locationName: 'UAE Power Grid (DEWA)',
    totalCapacityGW: 6.0,
    currentLoadGW: 2.2,
    suppliers: [
      {
        id: 'ae-masdar',
        name: 'Masdar Clean Energy',
        costPerKWh: 0.11,
        reliability: 0.995,
        greenEnergy: 0.85,
        maxOutput: 6000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.10 },
          { startHour: 18, endHour: 9, rate: 0.12 }
        ]
      },
      {
        id: 'ae-dewa',
        name: 'DEWA Business',
        costPerKWh: 0.09,
        reliability: 0.998,
        greenEnergy: 0.25,
        maxOutput: 15000,
        tariffSchedule: [
          { startHour: 7, endHour: 22, rate: 0.095 },
          { startHour: 22, endHour: 7, rate: 0.08 }
        ]
      },
      {
        id: 'ae-taqa',
        name: 'TAQA Industrial',
        costPerKWh: 0.075,
        reliability: 0.99,
        greenEnergy: 0.10,
        maxOutput: 25000,
        tariffSchedule: [
          { startHour: 6, endHour: 21, rate: 0.08 },
          { startHour: 21, endHour: 6, rate: 0.07 }
        ]
      }
    ]
  },
  'hongkong-grid': {
    id: 'hongkong-grid',
    locationName: 'Hong Kong Power Grid (CLP / HKE)',
    totalCapacityGW: 2.0,
    currentLoadGW: 0.9,
    suppliers: [
      {
        id: 'hk-clp-green',
        name: 'CLP Renewable Plus',
        costPerKWh: 0.17,
        reliability: 0.998,
        greenEnergy: 0.60,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.16 },
          { startHour: 18, endHour: 9, rate: 0.18 }
        ]
      },
      {
        id: 'hk-clp',
        name: 'CLP Power Business',
        costPerKWh: 0.14,
        reliability: 0.999,
        greenEnergy: 0.20,
        maxOutput: 12000,
        tariffSchedule: [
          { startHour: 7, endHour: 22, rate: 0.15 },
          { startHour: 22, endHour: 7, rate: 0.13 }
        ]
      },
      {
        id: 'hk-hke',
        name: 'HK Electric Bulk',
        costPerKWh: 0.125,
        reliability: 0.997,
        greenEnergy: 0.10,
        maxOutput: 18000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.13 },
          { startHour: 22, endHour: 6, rate: 0.115 }
        ]
      }
    ]
  },
  'japan-grid': {
    id: 'japan-grid',
    locationName: 'Japanese Power Grid (TEPCO)',
    totalCapacityGW: 12.0,
    currentLoadGW: 5.5,
    suppliers: [
      {
        id: 'jp-looop',
        name: 'Looop Green Denki',
        costPerKWh: 0.18,
        reliability: 0.995,
        greenEnergy: 0.80,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.17 },
          { startHour: 18, endHour: 9, rate: 0.19 }
        ]
      },
      {
        id: 'jp-tepco',
        name: 'TEPCO Energy Partner',
        costPerKWh: 0.15,
        reliability: 0.998,
        greenEnergy: 0.25,
        maxOutput: 15000,
        tariffSchedule: [
          { startHour: 8, endHour: 22, rate: 0.16 },
          { startHour: 22, endHour: 8, rate: 0.135 }
        ]
      },
      {
        id: 'jp-jera',
        name: 'JERA Wholesale',
        costPerKWh: 0.13,
        reliability: 0.996,
        greenEnergy: 0.12,
        maxOutput: 25000,
        tariffSchedule: [
          { startHour: 6, endHour: 22, rate: 0.14 },
          { startHour: 22, endHour: 6, rate: 0.12 }
        ]
      }
    ]
  },
  'australia-grid': {
    id: 'australia-grid',
    locationName: 'Australian Power Grid (AEMO NEM)',
    totalCapacityGW: 5.0,
    currentLoadGW: 2.0,
    suppliers: [
      {
        id: 'au-origin-green',
        name: 'Origin Green Power',
        costPerKWh: 0.14,
        reliability: 0.985,
        greenEnergy: 0.85,
        maxOutput: 5000,
        tariffSchedule: [
          { startHour: 9, endHour: 18, rate: 0.13 },
          { startHour: 18, endHour: 9, rate: 0.15 }
        ]
      },
      {
        id: 'au-agl',
        name: 'AGL Business',
        costPerKWh: 0.115,
        reliability: 0.99,
        greenEnergy: 0.35,
        maxOutput: 12000,
        tariffSchedule: [
          { startHour: 7, endHour: 22, rate: 0.12 },
          { startHour: 22, endHour: 7, rate: 0.10 }
        ]
      },
      {
        id: 'au-energyaustralia',
        name: 'EnergyAustralia Wholesale',
        costPerKWh: 0.10,
        reliability: 0.985,
        greenEnergy: 0.20,
        maxOutput: 20000,
        tariffSchedule: [
          { startHour: 6, endHour: 21, rate: 0.105 },
          { startHour: 21, endHour: 6, rate: 0.09 }
        ]
      }
    ]
  }
};

// Chiller types available for cooling systems
export const CHILLER_TYPES: ChillerType[] = [
  {
    id: 'basic-air',
    name: 'Basic Air Cooling',
    description: 'Standard air-cooled chiller system with moderate efficiency.',
    levels: [
      { level: 1, cost: 5000, coolingOutputKW: 20, powerConsumptionKW: 5, efficiency: 0.8 },
      { level: 2, cost: 15000, coolingOutputKW: 40, powerConsumptionKW: 9, efficiency: 0.82 },
      { level: 3, cost: 30000, coolingOutputKW: 80, powerConsumptionKW: 16, efficiency: 0.84 }
    ]
  },
  {
    id: 'water-cooled',
    name: 'Water-Cooled System',
    description: 'More efficient water-based cooling system with higher capacity.',
    levels: [
      { level: 1, cost: 12000, coolingOutputKW: 30, powerConsumptionKW: 6, efficiency: 0.85 },
      { level: 2, cost: 25000, coolingOutputKW: 60, powerConsumptionKW: 11, efficiency: 0.87 },
      { level: 3, cost: 50000, coolingOutputKW: 120, powerConsumptionKW: 20, efficiency: 0.9 }
    ]
  },
  {
    id: 'advanced-hybrid',
    name: 'Advanced Hybrid Cooling',
    description: 'High-end cooling solution combining air and liquid cooling technologies.',
    levels: [
      { level: 1, cost: 30000, coolingOutputKW: 50, powerConsumptionKW: 8, efficiency: 0.9 },
      { level: 2, cost: 60000, coolingOutputKW: 100, powerConsumptionKW: 15, efficiency: 0.92 },
      { level: 3, cost: 120000, coolingOutputKW: 200, powerConsumptionKW: 28, efficiency: 0.95 }
    ]
  }
];

// ── Cooling delivery chain ────────────────────────────────────────────────────
// Full chain: Heat Rejection (outdoor) → Chiller Plant → CRAH (in-room) → Racks.
// Effective cooling delivered = min(heatRejection × climate, Σ chillers, CRAH).
// Build order: Heat Rejection → Chiller → CRAH (each requires the previous stage).

// Heat Rejection — cooling towers / dry coolers that dump heat outdoors.
// Capacity is multiplied by the location's climate factor (free cooling in cold cities).
export const HEAT_REJECTION_LEVELS = [
  { level: 0, capacityKW: 0,    powerDrawKW: 0,  cost: 0 },
  { level: 1, capacityKW: 80,   powerDrawKW: 4,  cost: 12000 },
  { level: 2, capacityKW: 200,  powerDrawKW: 9,  cost: 35000 },
  { level: 3, capacityKW: 450,  powerDrawKW: 18, cost: 80000 },
  { level: 4, capacityKW: 1000, powerDrawKW: 36, cost: 180000 },
  { level: 5, capacityKW: 2200, powerDrawKW: 70, cost: 400000 },
];

// CRAH — in-room air handlers that deliver cold air to the floor.
export const CRAH_LEVELS = [
  { level: 0, capacityKW: 0,    powerDrawKW: 0,  cost: 0 },
  { level: 1, capacityKW: 60,   powerDrawKW: 3,  cost: 8000 },
  { level: 2, capacityKW: 150,  powerDrawKW: 7,  cost: 22000 },
  { level: 3, capacityKW: 350,  powerDrawKW: 15, cost: 55000 },
  { level: 4, capacityKW: 800,  powerDrawKW: 30, cost: 130000 },
  { level: 5, capacityKW: 1800, powerDrawKW: 60, cost: 300000 },
];

// Per-location climate factor for heat rejection: >1 = colder climate, more free cooling
// (higher effective capacity + lower cooling power); <1 = hotter climate, harder to cool.
export const LOCATION_CLIMATE_FACTORS: Record<string, number> = {
  stockholm: 1.45,   // Nordic — abundant free cooling
  warsaw:    1.25,
  london:    1.20,
  amsterdam: 1.20,
  suburban:  1.20,   // Rotterdam (NL)
  rotterdam: 1.18,   // Heerlen (NL)
  frankfurt: 1.15,
  industrial:1.15,   // Frankfurt key
  paris:     1.12,
  bucharest: 1.10,
  milan:     0.98,
  madrid:    0.85,   // hot, dry — harder to reject heat
  istanbul:  0.85,
  // Americas
  seattle:      1.30,  // cool maritime — excellent free cooling
  chicago:      1.15,
  newark:       1.10,
  ashburn:      1.05,
  losangeles:   0.90,
  dallas:       0.80,  // Texas heat
  santiago:     1.00,
  buenosaires:  0.95,
  saopaulo:     0.85,  // subtropical, humid
  // Africa / Middle East / Asia-Pacific
  johannesburg: 1.05,  // high altitude — mild despite latitude
  tokyo:        1.00,
  sydney:       0.95,
  hongkong:     0.80,  // hot + humid
  dubai:        0.65,  // desert heat — hardest place in the game to cool
};
export const DEFAULT_CLIMATE_FACTOR = 1.0;

// ── Utility billing realism ────────────────────────────────────────────────
// Commercial electricity bills split into an energy charge ($/kWh, already
// modelled via supplier tariffs) and a DEMAND charge billed on peak grid draw
// ($/kW of demand per month). Renewables that shave the grid peak also cut this.
export const DEMAND_CHARGE_PER_KW_MONTH = 9;   // $/kW/month on peak facility grid draw

// Water Usage Effectiveness — evaporative cooling towers consume water to reject
// heat. Litres of water per kWh of IT heat rejected. Hot/dry climates evaporate
// more (scaled down by the free-cooling climate factor). Only billed when a
// heat-rejection (cooling-tower) stage is installed.
export const WUE_LITRES_PER_KWH = 1.8;         // industry-typical water usage effectiveness
export const WATER_COST_PER_M3 = 2.1;          // $ per cubic metre (1000 L)

// Floor Designer cost constants
export const FLOOR_DESIGN_BASE_COST = 50000; // Base cost for new floor construction
export const FLOOR_DESIGN_RACK_UNIT_COST = 5000; // Cost per rack space
export const FLOOR_DESIGN_COOLING_UNIT_COST = 15000; // Cost per cooling unit

// Goals and achievements system

export const ALL_GOALS: Goal[] = [
  {
    id: 'first_rack',
    name: 'First Step',
    description: 'Purchase your first rack',
    category: 'starter',
    target: 1,
    reward: 5000,
    icon: 'build',
    progress: (_stats: Stats, locations: Record<string, LocationData>) => Object.values(locations).reduce((sum: number, loc: LocationData) => {
      const racks = loc.floors ? Object.values(loc.floors).reduce((sum2: number, floor: Floor) => sum2 + (floor.racks?.filter(r => r.type).length || 0), 0) : 0;
      return sum + racks;
    }, 0)
  },
  {
    id: 'first_server',
    name: 'Going Online',
    description: 'Install your first server',
    category: 'starter',
    target: 1,
    reward: 3000,
    icon: 'server',
    progress: (stats: Stats) => stats.totalServers
  },
  {
    id: 'first_connectivity',
    name: 'Connected',
    description: 'Build your Meet-Me Room and connect to a transit provider',
    category: 'starter',
    target: 1,
    reward: 15000,
    icon: 'globe',
    progress: (_stats: Stats, locations: Record<string, LocationData>) => {
      const hasTransit = Object.values(locations).some(loc =>
        loc.mmr?.built && (loc.mmr.transitProviders ?? []).length > 0
      );
      return hasTransit ? 1 : 0;
    }
  },
  {
    id: 'ten_servers',
    name: 'Small Farm',
    description: 'Install 10 servers',
    category: 'growth',
    target: 10,
    reward: 15000,
    icon: 'screen',
    progress: (stats: Stats) => stats.totalServers
  },
  {
    id: 'first_profit',
    name: 'In The Black',
    description: 'Grow your balance past $750,000',
    category: 'starter',
    target: 750000,
    reward: 10000,
    icon: 'money',
    progress: (_stats: Stats, _locations: Record<string, LocationData>, money: number) => money
  },
  {
    id: 'efficiency_master',
    name: 'Efficiency Master',
    description: 'Achieve 90% rack fill in any location',
    category: 'achievement',
    target: 90,
    reward: 50000,
    icon: 'bolt',
    progress: (_stats: Stats, locations: Record<string, LocationData>) => {
      let maxEfficiency = 0;
      Object.values(locations).forEach(location => {
        if (location.floors) {
          Object.values(location.floors).forEach(floor => {
            const totalServers = (floor.racks || []).reduce((sum: number, rack: Rack) => sum + (rack.servers?.length || 0), 0);
            const totalCapacity = (floor.racks || []).reduce((sum: number, rack: Rack) => sum + (rack.maxCapacity || 0), 0);
            const efficiency = totalCapacity > 0 ? (totalServers / totalCapacity) * 100 : 0;
            maxEfficiency = Math.max(maxEfficiency, efficiency);
          });
        }
      });
      return maxEfficiency;
    }
  },
  {
    id: 'ixp_peering',
    name: 'Peering Up',
    description: 'Connect to an Internet Exchange Point',
    category: 'achievement',
    target: 1,
    reward: 75000,
    icon: 'trending',
    progress: (_stats: Stats, locations: Record<string, LocationData>) => {
      const hasIxp = Object.values(locations).some(loc =>
        (loc.mmr?.ixpConnections ?? []).length > 0
      );
      return hasIxp ? 1 : 0;
    }
  },
  {
    id: 'millionaire',
    name: 'Five Million',
    description: 'Accumulate $5,000,000',
    category: 'achievement',
    target: 5000000,
    reward: 150000,
    icon: 'gem',
    progress: (_stats: Stats, _locations: Record<string, LocationData>, money: number) => money
  },
  {
    id: 'first_hire',
    name: 'First Employee',
    description: 'Hire your first staff member',
    category: 'staff',
    target: 1,
    reward: 25000,
    icon: 'user',
    progress: (_stats: Stats, _locations: Record<string, LocationData>, _money: number, _upgrades: Upgrades, extra?: any) =>
      extra?.staffCount ?? 0
  },
  {
    id: 'full_team',
    name: 'Full Team',
    description: 'Have at least one of each staff role hired simultaneously',
    category: 'staff',
    target: 4,
    reward: 150000,
    icon: 'users',
    progress: (_stats: Stats, _locations: Record<string, LocationData>, _money: number, _upgrades: Upgrades, extra?: any) =>
      extra?.staffRoleCount ?? 0
  },
  {
    id: 'happy_customers',
    name: 'Happy Customers',
    description: 'Reach 1,000 total customers across all servers',
    category: 'growth',
    target: 1000,
    reward: 80000,
    icon: 'smile',
    progress: (stats: Stats) => Math.floor(stats.totalCustomers)
  },
  {
    id: 'big_operation',
    name: 'Big Operation',
    description: 'Install 50 servers across all locations',
    category: 'growth',
    target: 50,
    reward: 200000,
    icon: 'factory',
    progress: (stats: Stats) => stats.totalServers
  },
  {
    id: 'global_reach',
    name: 'Global Reach',
    description: 'Have all 3 locations unlocked and operational',
    category: 'achievement',
    target: 3,
    reward: 500000,
    icon: 'globe',
    progress: (_stats: Stats, locations: Record<string, LocationData>) =>
      Object.values(locations).filter(l => l.unlocked).length
  },
  {
    id: 'revenue_powerhouse',
    name: 'Revenue Powerhouse',
    description: 'Achieve $200,000/day in server revenue',
    category: 'achievement',
    target: 200000,
    reward: 250000,
    icon: 'trending',
    progress: (stats: Stats) => stats.totalRevenue
  },
  {
    id: 'first_100k_day',
    name: 'Six Figures',
    description: 'Reach $100,000/day in server revenue',
    category: 'growth',
    target: 100000,
    reward: 100000,
    icon: 'trending',
    progress: (stats: Stats) => stats.totalRevenue
  },
  {
    id: 'fifty_servers',
    name: 'Server Farm',
    description: 'Install 50 servers across all locations',
    category: 'growth',
    target: 50,
    reward: 75000,
    icon: 'server',
    progress: (stats: Stats) => stats.totalServers
  },
  {
    id: 'two_million',
    name: 'Two Million',
    description: 'Accumulate $2,000,000 in the bank',
    category: 'achievement',
    target: 2000000,
    reward: 50000,
    icon: 'landmark',
    progress: (_stats: Stats, _locations: Record<string, LocationData>, money?: number) => money ?? 0
  },
  {
    id: 'multi_location',
    name: 'Expansion Mode',
    description: 'Have 3 or more locations unlocked',
    category: 'growth',
    target: 3,
    reward: 30000,
    icon: 'map',
    progress: (_stats: Stats, locations: Record<string, LocationData>) =>
      Object.values(locations).filter(l => l.unlocked).length
  }
];

// Floor quality types
export const FLOOR_QUALITY_TYPES: FloorQuality[] = [
  {
    id: 'basic',
    name: 'Basic Floor',
    description: 'Standard raised floor with basic cooling efficiency',
    cost: 0,
    coolingBonus: 1.0,
    color: 'gray',
    badge: 'basic'
  },
  {
    id: 'premium',
    name: 'Premium Floor',
    description: 'Enhanced raised floor with improved cooling efficiency',
    cost: 75000,
    coolingBonus: 1.25,
    color: 'blue',
    badge: 'premium',
    reputationBonus: 1.1
  },
  {
    id: 'enterprise',
    name: 'Enterprise Floor',
    description: 'Advanced raised floor with superior cooling efficiency and reputation bonus',
    cost: 250000,
    coolingBonus: 1.5,
    color: 'purple',
    badge: 'enterprise',
    reputationBonus: 1.25
  }
];

// ============================================================================
// STAFF SYSTEM
// ============================================================================

export const STAFF_ROLE_CONFIGS: StaffRoleConfig[] = [
  {
    role: 'technician',
    name: 'Technician',
    description: 'Monitors and repairs failed hardware. Each technician reduces repair time and can auto-repair one server every 5 minutes.',
    effect: '-30% repair time per technician, auto-repairs failed servers',
    monthlySalary: 5000,
    maxCount: 5,
    icon: 'Wrench',
  },
  {
    role: 'network_engineer',
    name: 'Network Engineer',
    description: 'Optimises network infrastructure and keeps customers satisfied.',
    effect: '+5% server revenue per engineer',
    monthlySalary: 7000,
    maxCount: 3,
    icon: 'Network',
  },
  {
    role: 'electrician',
    name: 'Electrician',
    description: 'Handles power maintenance and reduces power upgrade costs.',
    effect: '-10% power upgrade cost per electrician',
    monthlySalary: 6000,
    maxCount: 3,
    icon: 'Zap',
  },
  {
    role: 'security_officer',
    name: 'Security Officer',
    description: 'Reduces the chance of random adverse events such as power outages and equipment failures.',
    effect: '-20% random event chance per officer',
    monthlySalary: 5600,
    maxCount: 4,
    icon: 'Shield',
  },
];

export const STAFF_HIRE_SIGNING_BONUS_MONTHS = 1;

// ============================================================================
// CUSTOMER SATISFACTION
// ============================================================================

export const SATISFACTION_DECAY_PER_CHECK = 2;
export const SATISFACTION_RECOVERY_PER_CHECK = 1.5;
export const SATISFACTION_CHURN_THRESHOLD = 25;
export const SATISFACTION_CHURN_INTERVAL_TICKS = 600;
export const SATISFACTION_REVENUE_PENALTY_PER_POINT = 0.005;

// ============================================================================
// SERVER AGING
// ============================================================================

export const SERVER_AGING_MATURE_TICKS = 60 * 24 * 60;
export const SERVER_AGING_OLD_TICKS = 60 * 24 * 180;
export const SERVER_AGING_EOL_TICKS = 60 * 24 * 360;
export const SERVER_AGING_EFFICIENCY_DECAY = 0.02;
export const SERVER_AGING_CHECK_INTERVAL = 60 * 24 * 10;

// ============================================================================
// BACKBONE / DARK FIBER
// ============================================================================

export const BACKBONE_FIBER_TYPES: BackboneFiberType[] = [
  {
    id: '10g',
    name: '10G Dark Fiber',
    description: 'Single-mode 10G wavelength. Good for moderate inter-DC traffic.',
    capacityGbps: 10,
    costPerLink: 150000,
    monthlyFeePerLink: 5000,
    color: 'blue',
  },
  {
    id: '100g',
    name: '100G Dark Fiber',
    description: '100G DWDM link. Handles heavy inter-DC workloads.',
    capacityGbps: 100,
    costPerLink: 500000,
    monthlyFeePerLink: 15000,
    color: 'indigo',
  },
  {
    id: '400g',
    name: '400G Backbone',
    description: 'Carrier-grade 400G backbone. Eliminates transit dependency between DCs.',
    capacityGbps: 400,
    costPerLink: 1500000,
    monthlyFeePerLink: 40000,
    color: 'violet',
  },
];

// Approximate distances between location pairs (km). Used for cost scaling display.
// Key format: "locA|locB" where locA < locB alphabetically.
export const LOCATION_DISTANCES: Record<string, number> = {
  'amsterdam|rotterdam': 60,
  'amsterdam|industrial': 420,
  'amsterdam|london': 370,
  'amsterdam|paris': 510,
  'amsterdam|stockholm': 1460,
  'amsterdam|warsaw': 1150,
  'amsterdam|bucharest': 2000,
  'amsterdam|istanbul': 2800,
  'amsterdam|madrid': 1760,
  'amsterdam|milan': 1270,
  'industrial|london': 650,
  'industrial|paris': 480,
  'industrial|milan': 690,
  'industrial|warsaw': 910,
  'industrial|stockholm': 1390,
  'industrial|bucharest': 1600,
  'industrial|istanbul': 2360,
  'industrial|madrid': 1810,
  'london|paris': 340,
  'london|madrid': 1260,
  'london|milan': 1230,
  'london|stockholm': 1430,
  'london|warsaw': 1440,
  'london|bucharest': 2100,
  'london|istanbul': 2840,
  'paris|madrid': 1050,
  'paris|milan': 640,
  'paris|stockholm': 1740,
  'paris|warsaw': 1370,
  'paris|bucharest': 2070,
  'paris|istanbul': 2710,
  'madrid|milan': 1830,
  'madrid|stockholm': 2870,
  'madrid|warsaw': 2530,
  'madrid|bucharest': 2950,
  'madrid|istanbul': 3220,
  'milan|stockholm': 1890,
  'milan|warsaw': 1330,
  'milan|bucharest': 1450,
  'milan|istanbul': 1940,
  'stockholm|warsaw': 900,
  'stockholm|bucharest': 2000,
  'stockholm|istanbul': 2800,
  'warsaw|bucharest': 1080,
  'warsaw|istanbul': 1850,
  'bucharest|istanbul': 680,
  'rotterdam|industrial': 470,
  'rotterdam|amsterdam': 70,
  'rotterdam|london': 380,
  'rotterdam|paris': 510,
  'rotterdam|madrid': 1790,
  'rotterdam|milan': 1300,
  'rotterdam|stockholm': 1470,
  'rotterdam|warsaw': 1170,
  'rotterdam|bucharest': 2020,
  'rotterdam|istanbul': 2820,
};

// Geographic coordinates per location key [lon, lat] — single source for the
// world map and for backbone distances between pairs not in LOCATION_DISTANCES.
export const LOCATION_COORDS: Record<string, [number, number]> = {
  rotterdam:    [5.98, 50.88],    // Heerlen
  suburban:     [4.47, 51.92],    // Rotterdam
  amsterdam:    [4.90, 52.37],
  industrial:   [8.68, 50.11],    // Frankfurt
  london:       [-0.13, 51.51],
  paris:        [2.35, 48.86],
  madrid:       [-3.70, 40.42],
  milan:        [9.19, 45.47],
  stockholm:    [18.07, 59.33],
  warsaw:       [21.01, 52.23],
  bucharest:    [26.10, 44.43],
  istanbul:     [28.98, 41.01],
  losangeles:   [-118.24, 34.05],
  seattle:      [-122.33, 47.61],
  dallas:       [-96.80, 32.78],
  chicago:      [-87.63, 41.88],
  ashburn:      [-77.49, 39.04],
  newark:       [-74.17, 40.74],
  saopaulo:     [-46.63, -23.55],
  santiago:     [-70.67, -33.45],
  buenosaires:  [-58.38, -34.60],
  johannesburg: [28.05, -26.20],
  dubai:        [55.30, 25.27],
  hongkong:     [114.17, 22.32],
  tokyo:        [139.69, 35.69],
  sydney:       [151.21, -33.87],
};

// Great-circle distance between two location keys (km). Prefers the curated
// LOCATION_DISTANCES pair when present, otherwise computes haversine.
export function getLocationDistanceKm(a: string, b: string): number {
  const pairKey = [a, b].sort().join('|');
  const curated = LOCATION_DISTANCES[pairKey];
  if (curated !== undefined) return curated;
  const ca = LOCATION_COORDS[a];
  const cb = LOCATION_COORDS[b];
  if (!ca || !cb) return 999;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(cb[1] - ca[1]);
  const dLon = toRad(cb[0] - ca[0]);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(ca[1])) * Math.cos(toRad(cb[1])) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * 6371 * Math.asin(Math.sqrt(h)));
}

// ============================================================================
// RENEWABLE ENERGY
// ============================================================================

// 24-hour generation profiles: fraction of rated capacity output (0..1)
export const SOLAR_PROFILE = [0,0,0,0,0,0.05,0.15,0.35,0.55,0.70,0.85,0.95,1.0,0.95,0.85,0.70,0.55,0.35,0.15,0.05,0,0,0,0];
export const WIND_PROFILE  = [0.6,0.65,0.7,0.7,0.65,0.6,0.55,0.5,0.45,0.45,0.5,0.55,0.6,0.65,0.7,0.75,0.8,0.8,0.75,0.7,0.65,0.65,0.6,0.6];

// Per-location renewable yield multipliers (relative to base profiles).
// Solar: influenced by latitude, cloud cover, irradiance. Wind: coastal exposure, terrain.
export const LOCATION_RENEWABLE_FACTORS: Record<string, { solar: number; wind: number; label: string }> = {
  rotterdam:   { solar: 0.85, wind: 1.20, label: 'Coastal NL — windy, moderate sun' },
  suburban:    { solar: 0.85, wind: 1.15, label: 'Rotterdam suburbs — similar to Heerlen' },
  industrial:  { solar: 0.90, wind: 0.90, label: 'Frankfurt — inland, average conditions' },
  amsterdam:   { solar: 0.85, wind: 1.15, label: 'Amsterdam — coastal, good wind' },
  london:      { solar: 0.75, wind: 1.10, label: 'London — cloudy, moderate Atlantic wind' },
  paris:       { solar: 0.95, wind: 0.85, label: 'Paris — decent sun, sheltered' },
  madrid:      { solar: 1.40, wind: 1.00, label: 'Madrid — high solar irradiance' },
  milan:       { solar: 1.10, wind: 0.70, label: 'Milan — sunny Po valley, low wind' },
  stockholm:   { solar: 0.65, wind: 1.35, label: 'Stockholm — low sun, strong wind' },
  warsaw:      { solar: 0.80, wind: 1.05, label: 'Warsaw — continental, average' },
  bucharest:   { solar: 1.05, wind: 0.90, label: 'Bucharest — good sun, moderate wind' },
  istanbul:    { solar: 1.20, wind: 0.95, label: 'Istanbul — sunny, Bosphorus breeze' },
  losangeles:  { solar: 1.35, wind: 0.85, label: 'Los Angeles — abundant sun, light wind' },
  seattle:     { solar: 0.70, wind: 1.05, label: 'Seattle — overcast, moderate wind' },
  dallas:      { solar: 1.25, wind: 1.15, label: 'Dallas — strong sun, Texas wind corridor' },
  chicago:     { solar: 0.95, wind: 1.20, label: 'Chicago — the Windy City' },
  ashburn:     { solar: 1.05, wind: 0.85, label: 'Ashburn — decent sun, inland' },
  newark:      { solar: 1.00, wind: 1.00, label: 'Newark — coastal average' },
  saopaulo:    { solar: 1.10, wind: 0.85, label: 'São Paulo — good sun, sheltered plateau' },
  santiago:    { solar: 1.30, wind: 0.80, label: 'Santiago — Andean sun, valley shelter' },
  buenosaires: { solar: 1.10, wind: 1.15, label: 'Buenos Aires — sunny, Pampas wind' },
  johannesburg:{ solar: 1.35, wind: 0.90, label: 'Johannesburg — high-altitude sun' },
  dubai:       { solar: 1.45, wind: 0.85, label: 'Dubai — desert sun, best solar in the game' },
  hongkong:    { solar: 1.00, wind: 1.00, label: 'Hong Kong — subtropical, sea breeze' },
  tokyo:       { solar: 1.00, wind: 0.90, label: 'Tokyo — temperate, sheltered bay' },
  sydney:      { solar: 1.25, wind: 1.10, label: 'Sydney — sunny, coastal wind' },
};

export const RENEWABLE_TYPES: RenewableType[] = [
  {
    id: 'solar',
    name: 'Solar Panels',
    description: 'Rooftop PV array. Generates clean power during daylight hours.',
    capacityKW: 100,       // per unit
    costPerUnit: 80000,
    monthlyMaintenance: 500,
    hourlyProfile: SOLAR_PROFILE,
  },
  {
    id: 'wind',
    name: 'Wind Turbine',
    description: 'On-site small wind turbine. More consistent than solar, less dependent on daylight.',
    capacityKW: 200,       // per unit
    costPerUnit: 250000,
    monthlyMaintenance: 1500,
    hourlyProfile: WIND_PROFILE,
  },
];

// Green certification threshold: fraction of total power draw covered by renewables
export const GREEN_CERT_RENEWABLE_FRACTION = 0.5;
// Revenue bonus from green certification
export const GREEN_CERT_REVENUE_BONUS = 0.05;

// ============================================================================
// CONTRACTS — generated from seed archetypes
// ============================================================================

// Revenue premium per requirement flag (stacks additively)
const _R = (nawas: boolean, green: boolean, tor: boolean): number =>
  1 + (nawas ? 0.15 : 0) + (green ? 0.12 : 0) + (tor ? 0.10 : 0);
const _snap = (n: number): number => Math.round(n / 500) * 500;

type _Seed = {
  name: string; type: ClientType; desc: string;
  srv: number; rev: number; pen: number; traffic: number;
  uptime: number; fromDay: number; minTier?: number;
};

const _mk = (
  out: ContractTemplate[],
  id: { v: number },
  tier: ContractTier,
  s: _Seed,
  overrides: Partial<ContractTemplate>,
): void => {
  out.push({
    id: `ct_${id.v++}`,
    tier,
    clientName: s.name,
    clientType: s.type,
    description: s.desc,
    requiredServerCount: s.srv,
    minServerTier: s.minTier ?? (tier === 'bronze' ? 0 : tier === 'silver' ? 1 : tier === 'gold' ? 2 : 3),
    uptimeRequired: s.uptime,
    requiresGreenCert: false,
    trafficGbpsPerServer: s.traffic,
    revenuePerServerPerDay: s.rev,
    penaltyPerBreach: s.pen,
    durationDays: 30,
    availableFromDay: s.fromDay,
    expiresAfterDays: 12,
    ...overrides,
  });
};

const _buildContracts = (): ContractTemplate[] => {
  const out: ContractTemplate[] = [];
  const id = { v: 0 };
  const add = (tier: ContractTier, s: _Seed, o: Partial<ContractTemplate>) => _mk(out, id, tier, s, o);

  // ── BRONZE — Entry servers (tier 0), days 0–15 ─────────────────────────────
  const B: _Seed[] = [
    // SaaS
    { name: 'DevShop EU',       type: 'saas',          desc: 'Web app hosting for a fast-growing SaaS startup. Basic uptime, light traffic.',                srv: 2, rev: 2000, pen: 5000,  traffic: 0.05, uptime: 0.990, fromDay: 0  },
    { name: 'CodeFlow Labs',    type: 'saas',          desc: 'Developer tooling and CI/CD platform. Moderate traffic, entry SLA.',                           srv: 2, rev: 1800, pen: 4000,  traffic: 0.04, uptime: 0.990, fromDay: 1  },
    { name: 'AppSprint Ltd',    type: 'saas',          desc: 'Agile project management SaaS for small teams.',                                               srv: 2, rev: 1900, pen: 4000,  traffic: 0.05, uptime: 0.980, fromDay: 2  },
    { name: 'WebCraft Studio',  type: 'saas',          desc: 'No-code website builder platform with light traffic.',                                         srv: 3, rev: 2000, pen: 5000,  traffic: 0.06, uptime: 0.990, fromDay: 0  },
    { name: 'PushNotify GmbH',  type: 'saas',          desc: 'Push notification and in-app messaging SaaS.',                                                 srv: 2, rev: 1700, pen: 3500,  traffic: 0.03, uptime: 0.980, fromDay: 3  },
    { name: 'FormHero BV',      type: 'saas',          desc: 'Online form builder and survey platform. Low traffic, relaxed SLA.',                           srv: 2, rev: 1600, pen: 3000,  traffic: 0.03, uptime: 0.970, fromDay: 2  },
    { name: 'TaskBoard EU',     type: 'saas',          desc: 'Kanban task management for remote teams.',                                                     srv: 2, rev: 1750, pen: 3500,  traffic: 0.04, uptime: 0.980, fromDay: 1  },
    // Streaming (small)
    { name: 'PodBase EU',       type: 'streaming',     desc: 'Podcast hosting and RSS delivery CDN.',                                                        srv: 2, rev: 2200, pen: 4000,  traffic: 0.08, uptime: 0.980, fromDay: 0  },
    { name: 'TuneStream AS',    type: 'streaming',     desc: 'Independent music streaming startup with growing listener base.',                               srv: 3, rev: 2400, pen: 5000,  traffic: 0.12, uptime: 0.990, fromDay: 3  },
    { name: 'CastNet BV',       type: 'streaming',     desc: 'Live webcast and restream platform for creators.',                                             srv: 2, rev: 2100, pen: 4500,  traffic: 0.10, uptime: 0.980, fromDay: 2  },
    { name: 'ClipVault GmbH',   type: 'streaming',     desc: 'Short-form video hosting and delivery.',                                                       srv: 3, rev: 2300, pen: 4500,  traffic: 0.15, uptime: 0.980, fromDay: 4  },
    // Education
    { name: 'LearnHub EU',      type: 'education',     desc: 'Online learning platform for K-12 students. Seasonal traffic.',                                srv: 2, rev: 1600, pen: 3000,  traffic: 0.04, uptime: 0.970, fromDay: 0  },
    { name: 'EduStream GmbH',   type: 'education',     desc: 'Educational video lecture platform for universities.',                                          srv: 2, rev: 1800, pen: 3500,  traffic: 0.06, uptime: 0.970, fromDay: 2  },
    { name: 'CampusNet BV',     type: 'education',     desc: 'University portal and LMS cloud services.',                                                    srv: 3, rev: 1700, pen: 3000,  traffic: 0.05, uptime: 0.980, fromDay: 3  },
    { name: 'QuizMaster EU',    type: 'education',     desc: 'Online exam and quiz hosting platform.',                                                       srv: 2, rev: 1550, pen: 2500,  traffic: 0.03, uptime: 0.970, fromDay: 1  },
    // VPN (small)
    { name: 'PrivacyLite EU',   type: 'vpn',           desc: 'Consumer VPN — bandwidth-heavy per connection, basic SLA.',                                    srv: 2, rev: 2300, pen: 4000,  traffic: 0.30, uptime: 0.990, fromDay: 0  },
    { name: 'SafeRoute BV',     type: 'vpn',           desc: 'Privacy-first VPN for consumers and families.',                                                srv: 2, rev: 2100, pen: 3500,  traffic: 0.25, uptime: 0.990, fromDay: 3  },
    // Gaming (small)
    { name: 'IndieHost EU',     type: 'gaming',        desc: 'Multiplayer game server hosting for indie studios. Light traffic.',                             srv: 2, rev: 2000, pen: 4000,  traffic: 0.15, uptime: 0.990, fromDay: 0  },
    { name: 'GameJam Server',   type: 'gaming',        desc: 'Burst game servers for monthly game jam events.',                                              srv: 2, rev: 1800, pen: 3000,  traffic: 0.12, uptime: 0.970, fromDay: 2  },
    { name: 'PixelArena GmbH',  type: 'gaming',        desc: 'Competitive arena shooter dedicated servers.',                                                 srv: 3, rev: 2200, pen: 5000,  traffic: 0.20, uptime: 0.990, fromDay: 4  },
    { name: 'RetroLAN BV',      type: 'gaming',        desc: 'Classic game emulation and LAN party servers.',                                                srv: 2, rev: 1700, pen: 3000,  traffic: 0.10, uptime: 0.970, fromDay: 1  },
    // Healthcare (small)
    { name: 'ClinicApp EU',     type: 'healthcare',    desc: 'Patient scheduling and appointment SaaS for clinics.',                                         srv: 2, rev: 1900, pen: 5000,  traffic: 0.03, uptime: 0.990, fromDay: 2  },
    { name: 'PharmTrack BV',    type: 'healthcare',    desc: 'Pharmacy inventory and prescription SaaS.',                                                    srv: 2, rev: 1800, pen: 4500,  traffic: 0.02, uptime: 0.990, fromDay: 4  },
    // Cybersecurity (small)
    { name: 'ScanGuard EU',     type: 'cybersecurity', desc: 'Automated vulnerability scanning as a service.',                                               srv: 2, rev: 2200, pen: 5000,  traffic: 0.05, uptime: 0.990, fromDay: 0  },
    { name: 'LogWatch BV',      type: 'cybersecurity', desc: 'Real-time security log monitoring and alerting.',                                              srv: 2, rev: 2000, pen: 4500,  traffic: 0.04, uptime: 0.990, fromDay: 3  },
  ];

  B.forEach(s => {
    add('bronze', s, { durationDays: 14, expiresAfterDays: 10, revenuePerServerPerDay: s.rev,                                                                                                        description: s.desc + ' Short-term contract.' });
    add('bronze', s, { durationDays: 30, expiresAfterDays: 14, revenuePerServerPerDay: Math.round(s.rev * 1.05 / 100) * 100,                                                                        description: s.desc + ' Standard 30-day agreement.' });
    add('bronze', s, { durationDays: 45, expiresAfterDays: 14, revenuePerServerPerDay: Math.round(s.rev * 1.08 / 100) * 100, requiredServerCount: s.srv + 1, penaltyPerBreach: Math.round(s.pen * 1.2), description: s.desc + ' Extended 45-day engagement.' });
    add('bronze', s, { durationDays: 30, expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(true,false,false)), penaltyPerBreach: Math.round(s.pen * 1.15), requiresNawas: true,          description: s.desc + ' DDoS protection required by client.', availableFromDay: s.fromDay + 2 });
    add('bronze', s, { durationDays: 30, expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(false,false,true)), penaltyPerBreach: Math.round(s.pen * 1.10), requiresRedundantTor: true,   description: s.desc + ' Client demands MLAG-redundant rack connectivity.', availableFromDay: s.fromDay + 3, uptimeRequired: s.uptime + 0.005 });
  });

  // ── SILVER — Mid servers (tier 1), days 15–55 ──────────────────────────────
  const S: _Seed[] = [
    // SaaS
    { name: 'CloudBase BV',       type: 'saas',          desc: 'Managed cloud platform. Solid compute and 99.5% uptime required.',                             srv: 5,  rev: 7500,  pen: 12000,  traffic: 0.50, uptime: 0.995, fromDay: 20 },
    { name: 'DataSync AG',         type: 'saas',          desc: 'Enterprise data synchronisation and ETL platform for mid-market clients.',                     srv: 5,  rev: 7000,  pen: 11000,  traffic: 0.40, uptime: 0.995, fromDay: 22 },
    { name: 'WorkFlow GmbH',       type: 'saas',          desc: 'Business process automation and workflow orchestration SaaS.',                                  srv: 6,  rev: 7800,  pen: 13000,  traffic: 0.50, uptime: 0.995, fromDay: 25 },
    { name: 'FormStack EU',        type: 'saas',          desc: 'Enterprise form and document workflow platform.',                                               srv: 4,  rev: 6500,  pen: 10000,  traffic: 0.35, uptime: 0.990, fromDay: 18 },
    { name: 'ProcessHub Ltd',      type: 'saas',          desc: 'Low-code workflow builder for enterprise operations teams.',                                    srv: 5,  rev: 7200,  pen: 12000,  traffic: 0.45, uptime: 0.995, fromDay: 30 },
    { name: 'AnalyticsCore EU',    type: 'saas',          desc: 'Business intelligence and real-time analytics SaaS.',                                          srv: 6,  rev: 8000,  pen: 14000,  traffic: 0.60, uptime: 0.995, fromDay: 28 },
    // E-commerce
    { name: 'EuroShop GmbH',       type: 'ecommerce',     desc: 'European e-commerce platform with traffic spikes during sales events.',                        srv: 6,  rev: 8500,  pen: 18000,  traffic: 0.70, uptime: 0.995, fromDay: 25 },
    { name: 'ShopFast EU',         type: 'ecommerce',     desc: 'Flash-sale e-commerce platform, high concurrency during drops.',                               srv: 7,  rev: 9000,  pen: 20000,  traffic: 0.80, uptime: 0.995, fromDay: 30 },
    { name: 'MerchCloud BV',       type: 'ecommerce',     desc: 'B2B merchandise and wholesale marketplace.',                                                   srv: 5,  rev: 7500,  pen: 15000,  traffic: 0.60, uptime: 0.990, fromDay: 25 },
    { name: 'RetailNet AS',        type: 'ecommerce',     desc: 'Omnichannel retail backend — in-store and online unified inventory.',                          srv: 6,  rev: 8000,  pen: 16000,  traffic: 0.65, uptime: 0.995, fromDay: 28 },
    // Gaming
    { name: 'GameServe EU',        type: 'gaming',        desc: 'Dedicated game servers for mid-tier MMO titles. 24/7 uptime critical.',                        srv: 6,  rev: 8500,  pen: 15000,  traffic: 1.20, uptime: 0.999, fromDay: 20 },
    { name: 'LagFree GmbH',        type: 'gaming',        desc: 'Low-latency FPS and battle royale server hosting.',                                            srv: 8,  rev: 9500,  pen: 18000,  traffic: 1.50, uptime: 0.999, fromDay: 25 },
    { name: 'MultiPlay BV',        type: 'gaming',        desc: 'Cross-platform multiplayer relay and matchmaking infrastructure.',                              srv: 6,  rev: 8000,  pen: 14000,  traffic: 1.00, uptime: 0.999, fromDay: 22 },
    { name: 'ArenaNet EU',         type: 'gaming',        desc: 'Esports match servers with real-time stat tracking.',                                          srv: 5,  rev: 9000,  pen: 16000,  traffic: 1.30, uptime: 0.999, fromDay: 30 },
    { name: 'MatchMaker AG',       type: 'gaming',        desc: 'Competitive matchmaking backend for ranked play.',                                             srv: 5,  rev: 8200,  pen: 14000,  traffic: 0.80, uptime: 0.999, fromDay: 28 },
    { name: 'RaceTrack Online',    type: 'gaming',        desc: 'Racing simulation multiplayer — high tick rate servers.',                                      srv: 6,  rev: 8800,  pen: 15000,  traffic: 1.40, uptime: 0.999, fromDay: 32 },
    // VPN
    { name: 'SecurePass EU',       type: 'vpn',           desc: 'Business VPN with high-bandwidth exit nodes across Europe.',                                   srv: 6,  rev: 8800,  pen: 15000,  traffic: 2.50, uptime: 0.999, fromDay: 20 },
    { name: 'TunnelNet GmbH',      type: 'vpn',           desc: 'Enterprise VPN gateway with split-tunneling and audit logs.',                                  srv: 5,  rev: 8000,  pen: 13000,  traffic: 2.00, uptime: 0.999, fromDay: 22 },
    { name: 'ProxyShield BV',      type: 'vpn',           desc: 'Residential proxy network — very high aggregate bandwidth.',                                   srv: 7,  rev: 9500,  pen: 16000,  traffic: 3.00, uptime: 0.999, fromDay: 25 },
    // Healthcare
    { name: 'MedPortal EU',        type: 'healthcare',    desc: 'Telemedicine platform with patient data. GDPR strict, high uptime.',                          srv: 5,  rev: 7500,  pen: 20000,  traffic: 0.30, uptime: 0.999, fromDay: 20 },
    { name: 'HealthSync GmbH',     type: 'healthcare',    desc: 'Hospital EHR and patient records synchronisation.',                                            srv: 6,  rev: 8500,  pen: 22000,  traffic: 0.25, uptime: 0.999, fromDay: 25 },
    { name: 'ClinicalCloud BV',    type: 'healthcare',    desc: 'Clinical trial data platform with strict compliance requirements.',                             srv: 5,  rev: 8000,  pen: 25000,  traffic: 0.20, uptime: 0.999, fromDay: 30 },
    // Finance
    { name: 'PayRoute Ltd',        type: 'finance',       desc: 'Payment processing infrastructure. Even brief downtime triggers SLA breach.',                  srv: 5,  rev: 9500,  pen: 25000,  traffic: 0.60, uptime: 0.999, fromDay: 35 },
    { name: 'FinanceHub EU',       type: 'finance',       desc: 'Wealth management platform with real-time market data feeds.',                                 srv: 6,  rev: 10000, pen: 28000,  traffic: 0.70, uptime: 0.999, fromDay: 40 },
    // Telecom
    { name: 'NetLink BV',          type: 'telecom',       desc: 'Regional ISP backend, subscriber management and DNS.',                                         srv: 7,  rev: 9000,  pen: 18000,  traffic: 2.50, uptime: 0.999, fromDay: 35 },
    // Cybersecurity
    { name: 'ThreatWatch EU',      type: 'cybersecurity', desc: 'Threat intelligence feeds and SOC analytics platform.',                                        srv: 5,  rev: 8500,  pen: 18000,  traffic: 0.50, uptime: 0.999, fromDay: 30 },
    { name: 'PatchGuard GmbH',     type: 'cybersecurity', desc: 'Vulnerability patching and asset inventory SaaS.',                                             srv: 4,  rev: 7500,  pen: 15000,  traffic: 0.40, uptime: 0.995, fromDay: 28 },
    // Education (mid)
    { name: 'AcademiaCloud EU',    type: 'education',     desc: 'University research portal and collaborative learning tools.',                                  srv: 5,  rev: 7000,  pen: 10000,  traffic: 0.30, uptime: 0.990, fromDay: 22 },
    { name: 'TestEngine GmbH',     type: 'education',     desc: 'High-stakes online exam platform — critical during exam seasons.',                             srv: 6,  rev: 7500,  pen: 14000,  traffic: 0.40, uptime: 0.999, fromDay: 26 },
  ];

  S.forEach(s => {
    add('silver', s, { durationDays: 30,  expiresAfterDays: 12, revenuePerServerPerDay: s.rev,                                                                                                                description: s.desc });
    add('silver', s, { durationDays: 60,  expiresAfterDays: 14, revenuePerServerPerDay: Math.round(s.rev * 1.05 / 100) * 100,                                                                               description: s.desc + ' Medium-term 60-day agreement.' });
    add('silver', s, { durationDays: 45,  expiresAfterDays: 12, revenuePerServerPerDay: _snap(s.rev * _R(true,false,false)),  penaltyPerBreach: Math.round(s.pen * 1.15), requiresNawas: true,               description: s.desc + ' DDoS protection mandatory.', availableFromDay: s.fromDay + 5 });
    add('silver', s, { durationDays: 60,  expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(false,true,false)),  requiresGreenCert: true,                                                       description: s.desc + ' Client mandates green energy hosting.', availableFromDay: s.fromDay + 5 });
    add('silver', s, { durationDays: 90,  expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(true,false,true)),   penaltyPerBreach: Math.round(s.pen * 1.25), requiresNawas: true, requiresRedundantTor: true, requiredServerCount: s.srv + 1, uptimeRequired: Math.min(s.uptime + 0.001, 0.9999), description: s.desc + ' Full network redundancy and DDoS protection required.', availableFromDay: s.fromDay + 10 });
  });

  // ── GOLD — Perf/High servers (tier 2), days 50–130 ─────────────────────────
  const G: _Seed[] = [
    // Streaming (large)
    { name: 'StreamFlix EU',        type: 'streaming',     desc: 'CDN nodes for European video delivery. Peak evening load, 99.9% uptime.',                       srv: 10, rev: 16000, pen: 40000,  traffic: 2.20, uptime: 0.999,  fromDay: 60  },
    { name: 'VideoNation GmbH',     type: 'streaming',     desc: 'Live and on-demand video at scale. Adaptive bitrate, high throughput.',                          srv: 12, rev: 17000, pen: 45000,  traffic: 2.50, uptime: 0.999,  fromDay: 65  },
    { name: 'LiveCast BV',          type: 'streaming',     desc: 'Live sports and events broadcasting. Concurrent viewers in the millions.',                       srv: 14, rev: 19000, pen: 50000,  traffic: 3.00, uptime: 0.9999, fromDay: 70  },
    { name: 'BroadStream AS',       type: 'streaming',     desc: 'Multi-CDN streaming failover nodes. Always-on, global audience.',                                srv: 10, rev: 16500, pen: 40000,  traffic: 2.20, uptime: 0.999,  fromDay: 62  },
    { name: 'ContentHub EU',        type: 'streaming',     desc: 'Premium subscription streaming for 4K content across Europe.',                                   srv: 12, rev: 18000, pen: 48000,  traffic: 2.80, uptime: 0.999,  fromDay: 75  },
    // Finance
    { name: 'AlphaBank N.V.',       type: 'finance',       desc: 'Core banking infrastructure. Strict uptime, redundant connectivity mandatory.',                  srv: 12, rev: 22000, pen: 80000,  traffic: 2.00, uptime: 0.9999, fromDay: 90  },
    { name: 'TradeFast EU',         type: 'finance',       desc: 'High-frequency trading and low-latency market data infrastructure.',                             srv: 10, rev: 25000, pen: 100000, traffic: 1.50, uptime: 0.9999, fromDay: 90  },
    { name: 'ClearSettle GmbH',     type: 'finance',       desc: 'Payment clearing and settlement network. Sub-second latency required.',                          srv: 12, rev: 20000, pen: 75000,  traffic: 1.80, uptime: 0.9999, fromDay: 85  },
    { name: 'SecureBank BV',        type: 'finance',       desc: 'Digital banking platform. Strict compliance and fraud detection.',                               srv: 10, rev: 21000, pen: 70000,  traffic: 1.60, uptime: 0.9999, fromDay: 90  },
    // Gaming (large)
    { name: 'GameStorm EU',         type: 'gaming',        desc: 'Major MMO infrastructure. Thousands of concurrent players, peak weekends.',                      srv: 12, rev: 16000, pen: 45000,  traffic: 4.00, uptime: 0.999,  fromDay: 60  },
    { name: 'EpicArena GmbH',       type: 'gaming',        desc: 'Esports platform — ranked matches, live spectating, tournament brackets.',                       srv: 15, rev: 18000, pen: 55000,  traffic: 5.00, uptime: 0.999,  fromDay: 70  },
    { name: 'NetPlay EU',           type: 'gaming',        desc: 'Cross-region game relay network. Low-latency routing between players.',                          srv: 10, rev: 16500, pen: 40000,  traffic: 3.50, uptime: 0.999,  fromDay: 65  },
    { name: 'CloudGaming BV',       type: 'gaming',        desc: 'Game streaming compute nodes. GPU-rendered frames streamed to thin clients.',                    srv: 15, rev: 19000, pen: 60000,  traffic: 6.00, uptime: 0.999,  fromDay: 75, minTier: 2  },
    { name: 'TournamentHost AS',    type: 'gaming',        desc: 'Global esports tournament backend. Zero downtime during live events.',                           srv: 12, rev: 17500, pen: 50000,  traffic: 4.50, uptime: 0.999,  fromDay: 80  },
    { name: 'StrategyForge EU',     type: 'gaming',        desc: 'Real-time strategy game servers. High player counts and persistent world state.',                srv: 10, rev: 16000, pen: 42000,  traffic: 3.80, uptime: 0.999,  fromDay: 68  },
    // Telecom
    { name: 'TeleLink EU',          type: 'telecom',       desc: 'European mobile carrier data processing and charging cluster.',                                  srv: 15, rev: 18000, pen: 55000,  traffic: 8.00, uptime: 0.9999, fromDay: 70  },
    { name: 'NetCarrier GmbH',      type: 'telecom',       desc: 'National ISP core routing, AAA and subscriber management.',                                     srv: 12, rev: 19000, pen: 60000,  traffic: 10.0, uptime: 0.9999, fromDay: 75  },
    { name: 'FiberNet BV',          type: 'telecom',       desc: 'Fiber broadband provisioning and monitoring infrastructure.',                                    srv: 10, rev: 17000, pen: 50000,  traffic: 7.00, uptime: 0.9999, fromDay: 65  },
    { name: 'SignalCore AS',        type: 'telecom',       desc: 'Telecom signalling and SS7/Diameter infrastructure for MVNO clients.',                          srv: 12, rev: 18500, pen: 58000,  traffic: 9.00, uptime: 0.9999, fromDay: 80  },
    // Cybersecurity (large)
    { name: 'CyberShield EU',       type: 'cybersecurity', desc: 'Enterprise XDR and threat detection platform. Always-on monitoring.',                           srv: 10, rev: 17000, pen: 50000,  traffic: 2.00, uptime: 0.999,  fromDay: 60  },
    { name: 'ThreatGuard GmbH',     type: 'cybersecurity', desc: 'Nation-state threat intelligence and SIEM aggregation.',                                        srv: 12, rev: 18000, pen: 60000,  traffic: 2.50, uptime: 0.9999, fromDay: 70  },
    { name: 'SecureOps BV',         type: 'cybersecurity', desc: 'Managed security operations centre. 24/7 analysts, real-time telemetry.',                       srv: 10, rev: 16000, pen: 45000,  traffic: 1.80, uptime: 0.999,  fromDay: 65  },
    // VPN (large)
    { name: 'VaultVPN EU',          type: 'vpn',           desc: 'Enterprise VPN with geo-distributed high-bandwidth exit nodes.',                                 srv: 12, rev: 17000, pen: 45000,  traffic: 5.00, uptime: 0.999,  fromDay: 55  },
    { name: 'StealthNet GmbH',      type: 'vpn',           desc: 'Zero-log VPN for enterprise. High bandwidth, strict no-logging audited.',                       srv: 15, rev: 16500, pen: 40000,  traffic: 6.00, uptime: 0.999,  fromDay: 60  },
    // Healthcare (large)
    { name: 'NationalHealth Cloud', type: 'healthcare',    desc: 'National digital health records. Strict GDPR and HL7 compliance.',                              srv: 12, rev: 20000, pen: 80000,  traffic: 1.00, uptime: 0.9999, fromDay: 80  },
    { name: 'BioDataVault EU',      type: 'healthcare',    desc: 'Genomics and clinical research data storage and compute.',                                       srv: 10, rev: 18000, pen: 65000,  traffic: 0.80, uptime: 0.9999, fromDay: 85  },
    // Research
    { name: 'EuroSci Computing',    type: 'research',      desc: 'HPC cluster for climate and materials science research.',                                        srv: 15, rev: 20000, pen: 60000,  traffic: 8.00, uptime: 0.999,  fromDay: 90  },
    // SaaS (enterprise)
    { name: 'CloudPeak Solutions',  type: 'saas',          desc: 'Public cloud platform serving enterprise customers across Europe.',                              srv: 15, rev: 18000, pen: 50000,  traffic: 2.50, uptime: 0.999,  fromDay: 100 },
    { name: 'OmniOps EU',          type: 'saas',          desc: 'DevOps and platform engineering SaaS for large enterprises.',                                    srv: 12, rev: 17000, pen: 48000,  traffic: 2.00, uptime: 0.999,  fromDay: 95  },
  ];

  G.forEach(s => {
    add('gold', s, { durationDays: 60,  expiresAfterDays: 14, revenuePerServerPerDay: s.rev,                                                                                                                  description: s.desc });
    add('gold', s, { durationDays: 90,  expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(true,false,false)),  penaltyPerBreach: Math.round(s.pen * 1.15), requiresNawas: true,                 description: s.desc + ' DDoS protection mandatory.',             availableFromDay: s.fromDay + 10 });
    add('gold', s, { durationDays: 90,  expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(false,true,false)),  requiresGreenCert: true,                                                         description: s.desc + ' Green energy certification required.',   availableFromDay: s.fromDay + 10 });
    add('gold', s, { durationDays: 120, expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(true,true,false)),   penaltyPerBreach: Math.round(s.pen * 1.30), requiresNawas: true, requiresGreenCert: true, requiredServerCount: s.srv + 2, uptimeRequired: Math.min(s.uptime + 0.0001, 0.9999), minReputation: 75, description: s.desc + ' DDoS + green energy required. Reputation ≥75.', availableFromDay: s.fromDay + 15 });
    add('gold', s, { durationDays: 180, expiresAfterDays: 14, revenuePerServerPerDay: _snap(s.rev * _R(true,true,true)),    penaltyPerBreach: Math.round(s.pen * 1.50), requiresNawas: true, requiresGreenCert: true, requiresRedundantTor: true, requiredServerCount: s.srv + 3, uptimeRequired: 0.9999, minReputation: 80, description: s.desc + ' Maximum SLA — elite clients only. Reputation ≥80.', availableFromDay: s.fromDay + 20 });
  });

  // ── PLATINUM — High-end/GPU servers (tier 3+), days 150+ ───────────────────
  const P: (_Seed & { minTier: number })[] = [
    // AI / Research
    { name: 'DeepMind EU Research',    type: 'research',      desc: 'GPU cluster for large-scale AI model training. Highest compute density, strict green policy.',  srv: 8,  rev: 90000,  pen: 200000, traffic: 12.0, uptime: 0.999,  fromDay: 200, minTier: 4 },
    { name: 'NeuralCloud GmbH',        type: 'research',      desc: 'Distributed AI inference for enterprise. GPU-heavy, high throughput, green cert mandatory.',    srv: 10, rev: 80000,  pen: 180000, traffic: 10.0, uptime: 0.999,  fromDay: 190, minTier: 4 },
    { name: 'DataForge EU',            type: 'research',      desc: 'Petascale data processing for scientific simulations across European research institutes.',      srv: 15, rev: 50000,  pen: 120000, traffic: 15.0, uptime: 0.999,  fromDay: 170, minTier: 3 },
    { name: 'ComputeAI BV',            type: 'research',      desc: 'Autonomous vehicle perception training — massive GPU demand, always-on.',                       srv: 12, rev: 75000,  pen: 150000, traffic: 12.0, uptime: 0.999,  fromDay: 210, minTier: 4 },
    { name: 'QuantumCore EU',          type: 'research',      desc: 'Quantum algorithm simulation requiring maximum compute density and uptime.',                     srv: 8,  rev: 100000, pen: 250000, traffic: 20.0, uptime: 0.9999, fromDay: 250, minTier: 5 },
    { name: 'GenomicsHPC GmbH',        type: 'research',      desc: 'Genomics sequencing and bioinformatics HPC. Large storage and GPU workloads.',                  srv: 10, rev: 65000,  pen: 140000, traffic: 10.0, uptime: 0.999,  fromDay: 180, minTier: 4 },
    // Government
    { name: 'EU Digital Agency',       type: 'government',    desc: 'European government cloud infrastructure. Green energy and 99.99% uptime required.',           srv: 20, rev: 35000,  pen: 120000, traffic: 4.50, uptime: 0.9999, fromDay: 180, minTier: 3 },
    { name: 'NatSec Cloud',            type: 'government',    desc: 'National security analytics platform — maximum uptime and full redundancy.',                    srv: 16, rev: 40000,  pen: 150000, traffic: 5.00, uptime: 0.9999, fromDay: 200, minTier: 3 },
    { name: 'CivicData EU',            type: 'government',    desc: 'Cross-border government data exchange infrastructure for EU member states.',                    srv: 20, rev: 32000,  pen: 100000, traffic: 3.50, uptime: 0.9999, fromDay: 175, minTier: 3 },
    { name: 'JusticeCloud GmbH',       type: 'government',    desc: 'Court records and legal AI processing for EU judicial institutions.',                           srv: 15, rev: 36000,  pen: 110000, traffic: 3.00, uptime: 0.9999, fromDay: 190, minTier: 3 },
    // Streaming (enterprise)
    { name: 'StreamVault EU',          type: 'streaming',     desc: 'Premium subscription streaming — 4K/8K delivery nodes at massive scale.',                      srv: 25, rev: 38000,  pen: 100000, traffic: 8.00, uptime: 0.9999, fromDay: 165, minTier: 3 },
    { name: 'MediaGiant GmbH',         type: 'streaming',     desc: 'Major media company streaming and live distribution platform across Europe.',                   srv: 30, rev: 36000,  pen: 90000,  traffic: 7.00, uptime: 0.9999, fromDay: 175, minTier: 3 },
    { name: 'ContentTitan BV',         type: 'streaming',     desc: 'Tier-1 CDN for European content delivery. Billions of requests per day.',                      srv: 30, rev: 35000,  pen: 85000,  traffic: 8.00, uptime: 0.9999, fromDay: 165, minTier: 3 },
    // Telecom (enterprise)
    { name: 'TeleCorpEU',             type: 'telecom',       desc: 'Pan-European carrier-grade IMS and core network — 20 Gbps+ per node.',                        srv: 25, rev: 42000,  pen: 150000, traffic: 20.0, uptime: 0.9999, fromDay: 175, minTier: 3 },
    { name: 'NetGlobal GmbH',          type: 'telecom',       desc: 'Tier-1 ISP backbone routing and traffic engineering at continental scale.',                    srv: 20, rev: 45000,  pen: 160000, traffic: 20.0, uptime: 0.9999, fromDay: 200, minTier: 3 },
    // Gaming (AAA / massive launch)
    { name: 'MegaPlay EU',             type: 'gaming',        desc: 'AAA game launch infrastructure — global burst capacity for day-one releases.',                  srv: 30, rev: 38000,  pen: 100000, traffic: 10.0, uptime: 0.9999, fromDay: 175, minTier: 3 },
    { name: 'MetaverseCore BV',        type: 'gaming',        desc: 'Persistent metaverse platform — millions of concurrent users, physics simulations.',           srv: 35, rev: 40000,  pen: 120000, traffic: 12.0, uptime: 0.9999, fromDay: 200, minTier: 3 },
    { name: 'WorldEngine GmbH',        type: 'gaming',        desc: 'Massively multiplayer open world — dedicated simulation servers for 500k+ players.',           srv: 25, rev: 42000,  pen: 110000, traffic: 11.0, uptime: 0.9999, fromDay: 180, minTier: 3 },
    // VPN (enterprise)
    { name: 'FortressVPN EU',          type: 'vpn',           desc: 'Enterprise zero-trust VPN. 50+ Gbps aggregate, audited no-log, green cert required.',          srv: 20, rev: 36000,  pen: 90000,  traffic: 8.00, uptime: 0.9999, fromDay: 165, minTier: 3 },
    // Cybersecurity (enterprise)
    { name: 'FortressNet EU',          type: 'cybersecurity', desc: 'Global SOC and threat hunting platform. Real-time intelligence for Fortune 500 clients.',      srv: 15, rev: 38000,  pen: 120000, traffic: 3.00, uptime: 0.9999, fromDay: 175, minTier: 3 },
    { name: 'ZeroDay Shield GmbH',     type: 'cybersecurity', desc: 'Zero-day exploit detection and rapid response infrastructure.',                                 srv: 12, rev: 40000,  pen: 130000, traffic: 2.50, uptime: 0.9999, fromDay: 185, minTier: 3 },
    // SaaS (enterprise)
    { name: 'GreenTech Unicorn',       type: 'saas',          desc: 'ESG-focused enterprise SaaS. Full green energy, NaWas DDoS and maximum uptime mandatory.',    srv: 25, rev: 38000,  pen: 150000, traffic: 5.00, uptime: 0.9999, fromDay: 240, minTier: 3 },
    { name: 'HyperScale EU',           type: 'saas',          desc: 'Hyperscale SaaS platform replacing legacy enterprise software across 50+ countries.',          srv: 30, rev: 37000,  pen: 140000, traffic: 4.50, uptime: 0.9999, fromDay: 220, minTier: 3 },
  ];

  P.forEach(s => {
    add('platinum', s, { durationDays: 180, expiresAfterDays: 21, minServerTier: s.minTier, revenuePerServerPerDay: s.rev,                                                                                                                      description: s.desc });
    add('platinum', s, { durationDays: 180, expiresAfterDays: 21, minServerTier: s.minTier, revenuePerServerPerDay: _snap(s.rev * _R(false,true,false)),  requiresGreenCert: true,  minReputation: 80,                                         description: s.desc + ' Green energy certification required. Reputation ≥80 needed.',           availableFromDay: s.fromDay + 10 });
    add('platinum', s, { durationDays: 365, expiresAfterDays: 21, minServerTier: s.minTier, revenuePerServerPerDay: _snap(s.rev * _R(true,true,false)),   penaltyPerBreach: Math.round(s.pen * 1.15), requiresNawas: true, requiresGreenCert: true,  minReputation: 85,          description: s.desc + ' DDoS protection and green energy required. Reputation ≥85.',     availableFromDay: s.fromDay + 15 });
    add('platinum', s, { durationDays: 365, expiresAfterDays: 21, minServerTier: s.minTier, revenuePerServerPerDay: _snap(s.rev * _R(true,true,true)),    penaltyPerBreach: Math.round(s.pen * 1.40), requiresNawas: true, requiresGreenCert: true, requiresRedundantTor: true, requiredServerCount: s.srv + Math.ceil(s.srv * 0.15), uptimeRequired: 0.9999, minReputation: 90, description: s.desc + ' Maximum SLA — elite clients only. Reputation ≥90 required.', availableFromDay: s.fromDay + 20 });
    add('platinum', s, { durationDays: 30,  expiresAfterDays: 14, minServerTier: s.minTier, revenuePerServerPerDay: _snap(s.rev * 1.30), requiredServerCount: Math.max(2, Math.round(s.srv * 0.5)), description: s.desc + ' Short burst contract — premium rate, smaller footprint.', availableFromDay: s.fromDay });
  });

  return out;
};

// Servers earn $0 without a contract. All hosting revenue flows through contracts.
const HYPERSCALE_CONTRACTS: ContractTemplate[] = [
  {
    id: 'hs_global_cdn',
    tier: 'hyperscale',
    clientName: 'GlobalEdge CDN',
    clientType: 'streaming',
    description: 'Worldwide CDN presence requiring PoPs in at least 2 of your datacenters for low-latency delivery.',
    requiredServerCount: 20,
    minServerTier: 2,
    multiLocation: true,
    requiredLocations: 2,
    uptimeRequired: 0.999,
    requiresGreenCert: false,
    requiresNawas: true,
    trafficGbpsPerServer: 3.0,
    revenuePerServerPerDay: 28_000,
    penaltyPerBreach: 120_000,
    durationDays: 180,
    availableFromDay: 60,
    expiresAfterDays: 14,
  },
  {
    id: 'hs_hyperscale_cloud',
    tier: 'hyperscale',
    clientName: 'NimbusCloud',
    clientType: 'saas',
    description: 'Cloud-native SaaS platform demanding geo-redundant hosting across 3 European datacenters.',
    requiredServerCount: 15,
    minServerTier: 3,
    multiLocation: true,
    requiredLocations: 3,
    uptimeRequired: 0.9999,
    requiresGreenCert: false,
    requiresNawas: true,
    requiresRedundantTor: true,
    trafficGbpsPerServer: 5.0,
    revenuePerServerPerDay: 42_000,
    penaltyPerBreach: 200_000,
    durationDays: 365,
    availableFromDay: 90,
    expiresAfterDays: 14,
  },
  {
    id: 'hs_intl_finance',
    tier: 'hyperscale',
    clientName: 'ArcticBank',
    clientType: 'finance',
    description: 'International clearing house requiring dual-DC redundancy, green certification, and DDoS protection.',
    requiredServerCount: 12,
    minServerTier: 3,
    multiLocation: true,
    requiredLocations: 2,
    uptimeRequired: 0.9999,
    requiresGreenCert: true,
    requiresNawas: true,
    requiresRedundantTor: true,
    minReputation: 85,
    trafficGbpsPerServer: 4.0,
    revenuePerServerPerDay: 55_000,
    penaltyPerBreach: 300_000,
    durationDays: 240,
    availableFromDay: 120,
    expiresAfterDays: 10,
  },
  {
    id: 'hs_gaming_network',
    tier: 'hyperscale',
    clientName: 'PixelArena Games',
    clientType: 'gaming',
    description: 'AAA game publisher launching a global title. Requires 2 DCs with MLAG redundancy for match-making servers.',
    requiredServerCount: 25,
    minServerTier: 2,
    multiLocation: true,
    requiredLocations: 2,
    uptimeRequired: 0.999,
    requiresGreenCert: false,
    requiresNawas: false,
    requiresRedundantTor: true,
    trafficGbpsPerServer: 2.5,
    revenuePerServerPerDay: 32_000,
    penaltyPerBreach: 150_000,
    durationDays: 120,
    availableFromDay: 45,
    expiresAfterDays: 14,
  },
  {
    id: 'hs_gov_cloud',
    tier: 'hyperscale',
    clientName: 'EU Digital Services',
    clientType: 'government',
    description: 'European government cloud mandate: sovereign infrastructure across 3 DCs, green-certified, maximum redundancy.',
    requiredServerCount: 10,
    minServerTier: 3,
    multiLocation: true,
    requiredLocations: 3,
    uptimeRequired: 0.9999,
    requiresGreenCert: true,
    requiresNawas: true,
    requiresRedundantTor: true,
    minReputation: 80,
    trafficGbpsPerServer: 3.0,
    revenuePerServerPerDay: 70_000,
    penaltyPerBreach: 400_000,
    durationDays: 480,
    availableFromDay: 150,
    expiresAfterDays: 10,
  },
  {
    id: 'hs_ai_grid',
    tier: 'hyperscale',
    clientName: 'Synapse AI',
    clientType: 'research',
    description: 'Distributed AI training cluster requiring GPU-class servers across 2 DCs for parallel model training.',
    requiredServerCount: 6,
    minServerTier: 4,
    multiLocation: true,
    requiredLocations: 2,
    uptimeRequired: 0.995,
    requiresGreenCert: true,
    requiresNawas: false,
    minReputation: 75,
    trafficGbpsPerServer: 12.0,
    revenuePerServerPerDay: 180_000,
    penaltyPerBreach: 500_000,
    durationDays: 90,
    availableFromDay: 100,
    expiresAfterDays: 14,
  },
];

export const CONTRACT_TEMPLATES: ContractTemplate[] = [..._buildContracts(), ...HYPERSCALE_CONTRACTS];

// How many contracts are shown in the marketplace at once
export const CONTRACT_MARKETPLACE_SIZE = 4;
// How often (in game days) the marketplace refreshes
export const CONTRACT_REFRESH_DAYS = 3;

// ============================================================================
// LOANS
// ============================================================================

export interface LoanOption {
  id: string;
  label: string;
  principal: number;
  annualRate: number;   // expressed as annual multiplier; daily cost = principal × annualRate / 365
  termDays: number;     // in-game days (1 day = 6 real minutes at 1× speed)
  description: string;
}

// Rates are calibrated to in-game time, not real-world finance.
// 1 in-game year ≈ 36 real hours, so "annual" rates of 72–120% produce
// daily interest that is meaningfully painful vs typical daily revenues.
export const LOAN_OPTIONS: LoanOption[] = [
  {
    id: 'loan_small',
    label: 'Emergency Bridge Loan',
    principal: 100_000,
    annualRate: 3.00,   // 25 %/month → ~$822/day
    termDays: 45,
    description: '$100k cash injection at punishing rates. Costs ~$822/day — you need at least $5k/day revenue just to keep your head above water. Repay within 45 days or face serious losses.',
  },
  {
    id: 'loan_medium',
    label: 'Growth Capital',
    principal: 500_000,
    annualRate: 2.40,   // 20 %/month → ~$3,288/day
    termDays: 75,
    description: '$500k to fund expansion. ~$3,288/day in interest — you need $20k+/day revenue to carry this without bleeding out. High risk, high reward.',
  },
  {
    id: 'loan_large',
    label: 'Corporate Credit Line',
    principal: 2_000_000,
    annualRate: 2.16,   // 18 %/month → ~$11,836/day
    termDays: 120,
    description: '$2M for serious scaling. ~$11,800/day interest. Only take this if you are already generating $75k+/day. Total interest burden exceeds $1.4M.',
  },
  {
    id: 'loan_enterprise',
    label: 'Institutional Bond',
    principal: 10_000_000,
    annualRate: 1.80,   // 15 %/month → ~$49,315/day
    termDays: 180,
    description: '$10M hyperscale capital. ~$49,300/day in interest — nearly $8.9M total. Reserved for operations generating $500k+/day. Mistime this and you go bankrupt.',
  },
];

// ============================================================================
// HARDWARE PROCUREMENT DELAYS (in-game days by server unlock tier)
// Technicians reduce delay by 1 day each (min 0).
// ============================================================================

export const DELIVERY_DAYS_BY_TIER: Record<number, number> = {
  0: 0,   // Entry (R250/R350) — same day
  1: 2,   // Mid (R450/R550)
  2: 5,   // Performance (R650/R650xs)
  3: 7,   // High-end (R750/R750xs/storage)
  4: 10,  // GPU (R750xa)
  5: 12,  // GPU Pro (R760xa)
  6: 21,  // AI (XE9680)
};
